import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DoCheck,
    ElementRef,
    HostListener,
    OnDestroy,
    ViewChild
} from '@angular/core';
import {RouteConfigLoadEnd, Router} from '@angular/router';
import {
    faBars,
    faBug,
    faCamera,
    faCheck,
    faCodeBranch,
    faColumns,
    faCommentDots,
    faCompress,
    faCopy,
    faCrosshairs,
    faDownload,
    faExpand,
    faMicrophone,
    faPaperPlane,
    faPaperclip,
    faPen,
    faPlus,
    faRedo,
    faRobot,
    faArchive,
    faSearch,
    faStop,
    faThumbsDown,
    faThumbsUp,
    faThumbtack,
    faTimes,
    faTrash,
    faUndo,
    faWindowRestore
} from '@fortawesome/free-solid-svg-icons';
import {SatPopoverComponent} from '@ncstate/sat-popover';
import {Subscription} from 'rxjs';
import {AssistantDataWindow, AssistantService, AssistantTaskStatus} from 'app/blocks/service/api/assistant.service';
import {Principal} from 'app/core/auth/principal.service';
import {RoleService} from 'app/blocks/service/api/role.service';
import {
    addContext,
    AddMode,
    addScreenshot,
    baseName,
    canAddContext,
    canAddScreenshot,
    isScreenFile,
    MAX_CONTEXTS,
    MAX_SCREENSHOTS,
    PendingAttachment,
    removeAttachment,
    SCREEN_FILE,
    SentAttachment,
    sentAttachments,
    turnPayload,
    withRoutesHint
} from 'app/blocks/service/assistant-context/assistant-attachments';
import {AssistantAttachmentsService} from 'app/blocks/service/assistant-context/assistant-attachments.service';
import {tenantDateFormat} from 'app/blocks/service/assistant-context/assistant-diagnostics';
import {
    AssistantScreenContext,
    contextSuggestions,
    matchRoute,
    routesHint,
    splitLinks,
    TextSegment,
    topLevelRoutes,
    unloadedLazyRoutes
} from 'app/blocks/service/assistant-context/assistant-context';
import {AssistantContextService} from 'app/blocks/service/assistant-context/assistant-context.service';
import {AssistantRoutePreloader} from 'app/blocks/service/assistant-context/assistant-route-preloader.service';
import {
    captureFailureReason,
    captureVisibleScreen,
    screenFileName,
    ScreenshotTimeoutError,
    toScreenRequest
} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-screen';
import {assistantErrorReason} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-errors';
import {MdBlock, parseMarkdown} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-markdown';
import {TranslateService} from '@ngx-translate/core';
import {
    ACTIVITY_TRANSLATIONS_EN,
    activityElapsed,
    activityLabel,
    activityStep,
    activityText,
    AssistantActivityView,
    toActivity
} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-activity';
import {
    isTerminalTaskState,
    liveStatusText,
    LOST_CONNECTION_TEXT,
    isRetryablePollAnswer,
    onPollFailure,
    POLL_INTERVAL_MS,
    waitingText
} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-poll';
import {
    askRecheckKey,
    createAskRecheck,
    createStripWatcher,
    needsScreenAnswer,
    revealScrollLeft,
    screenAskKey
} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-tabs';
import {
    assistantModeText,
    canActOnPlan,
    planErrorDetail,
    planGoneState,
    PlanCard,
    planStateLabel,
    restorePlanCard,
    stripPlanBlock,
    toPlanCard
} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-plan';
import {actionsVisible, chatModeText, editAllowed} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-capabilities';
import {
    BUG_DRAFT_LABEL,
    BUG_DRAFT_PROMPT,
    BugCard,
    bugReportBody,
    bugScreenshots,
    bugSubmitError,
    canSubmitBug,
    emptyBugDraft,
    explainsError,
    parseBugBlock,
    restoreBugCard,
    stripBugBlock,
    toBugDraft
} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-bug';
import {
    applyUndoStatus,
    canUndo,
    changeLines,
    ChangesCard,
    effectiveUndoCard,
    restoreChangesCard,
    serverReason,
    settleProposedUndos,
    stripChangesBlock,
    toChangesCard,
    undoErrorState,
    undoPlanOf,
    undoPlanFor,
    undoStateLabel,
    undoTaskIdOf
} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-changes';
import {
    applyRating,
    cancelledOutcome,
    claimResolution,
    completedTurns,
    dismissNotice,
    entryPointOf,
    EntryPoint,
    EVENT_FLUSH_MS,
    escalateUsed,
    FEEDBACK_REASONS,
    newMessageId,
    noticeDismissed,
    noticeKey,
    NOTICE_TEXT,
    reasonsBody,
    ReplyFeedback,
    requestStop,
    resolutionBody,
    Resolution,
    RESOLUTION_AUTO_DISMISS_MS,
    retryEntry,
    retryText,
    thumbBody,
    Thumb,
    toggleReason,
    turnExtras,
    UiEventBuffer,
    wantsEscalate
} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-analytics';
import {
    addClosedKey,
    freeSessionLabel,
    fromTranscript,
    isDeletedOnServer,
    isTranscriptFile,
    mergePlan,
    parseDeletedSessions,
    parseServerSessions,
    placeServerLabel,
    readRevision,
    ServerSessionInfo,
    serverSessionLabel,
    toTranscript,
    TRANSCRIPT_SAVE_DEBOUNCE_MS
} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-history';
import {
    assistantLoggedOut$,
    assistantStorageKey,
    isImeComposing,
    mergeStoredSessions,
    sessionsToKeepAfterSync,
    STORAGE_PREFIX
} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-storage';
import {ASSISTANT_LABEL} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-label';
import {
    clampWidthPct,
    cleanLabel,
    dragHasFiles,
    growHeight,
    HistoryItem,
    HistoryRow,
    historyRows,
    isAutoLabel,
    LAYOUT_KEY_BASE,
    NARROW_PANEL_PX,
    panelWidthPx,
    PanelLayout,
    pastedFileName,
    readLayout,
    titleFromQuestion,
    UPLOAD_AT_ONCE_MAX,
    widthPctFromPointer
} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-dock';
import {AssistantDictation, ensureMicrophone, isVoiceSupported} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-voice';
import {
    answeredByLabel,
    AnsweredBy,
    effectiveTier,
    normalizeTier,
    parseAnsweredBy,
    readTier,
    saveTier,
    TierOption,
    TierPreference,
    tierField,
    tierOptionsFor
} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-tier';
import {
    copyFormatted,
    copyPayload,
    csvFileName,
    downloadCsv,
    tableToCsv
} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-export';
import {
    branchFamily,
    branchPosition,
    editResendPlan,
    forkRequestFrom
} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-branch';
import {
    downloadErrorText,
    fileNameOf,
    fileSizeLabel,
    outputBaseline,
    OutputFileChip,
    producedSince,
    saveBlob,
    splitFiles,
    unclaimedOutputs
} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-files';

interface ChatMessage {
    role: 'user' | 'assistant';
    text: string;
    /** what the bubble shows instead of `text` (the "Report a bug" turn sends a long instruction) */
    display?: string;
    /** a drafted bug report: editable form, filed only on Submit (kept in the transcript) */
    bug?: BugCard;
    /** what an approved change did (before → after) with Undo (kept in the transcript) */
    changes?: ChangesCard;
    pending?: boolean;
    error?: boolean;
    /** change proposed by this reply; shown as a card with Approve / Cancel (kept in the transcript) */
    plan?: PlanCard;
    /** the assistant asked to see the user's screen: consent card, then a short note of what happened */
    screen?: ScreenAsk;
    /** local note in the transcript (e.g. "screenshot failed"); never sent */
    note?: boolean;
    /** pills of what the user attached to this message (screen contexts, screenshots); kept in the transcript */
    attachments?: SentAttachment[];
    /** F3: files this reply produced (`outputs/` of the chat session), shown as download chips (kept in the transcript) */
    outputFiles?: OutputFileChip[];
    /** RMA-61: what the running turn is doing ("Running a report query", step, start) - transient, never stored */
    activity?: AssistantActivityView;
    /** CU-14zefhy26bz: id and creation time of the message, and the turn / task it belongs to (joins the UI to the server's analytics) */
    id?: string;
    createdAt?: number;
    /** when the reply finished (idle detection); `createdAt` is when the turn started */
    completedAt?: number;
    turnId?: string;
    taskId?: string;
    /** thumbs / reasons given to this reply (kept in the transcript) */
    feedback?: ReplyFeedback;
    /** the user pressed Stop on this turn: "Stopping…" while it runs, then the bubble reads "Stopped." */
    stopped?: boolean;
    /** a failed turn remembers its question, so Retry can resend it */
    retryOf?: string;
    /** "copied" tick on the copy button (transient, never stored) */
    copied?: boolean;
    /** CU-14zefhy38pt W3: which tier and model answered (kept in the transcript) */
    answeredBy?: AnsweredBy;
    /** CU-14zefhy38pt W4 (C3): the period this answer covers (the server limits one question to `days` days) */
    dataWindow?: AssistantDataWindow;
    /** CU-14zefhy38pt W4 (C2): the "Request full history" request for this answer */
    historyAsk?: {state: 'sending' | 'sent' | 'failed'; taskUrl?: string; error?: string};
    /** CU-14zefhy38pt W3: the question is being edited ("Edit and resend"); transient */
    editing?: boolean;
    editText?: string;
}

type ScreenAskState = 'asking' | 'sharing' | 'shared' | 'declined' | 'expired' | 'failed' | 'timedOut';

interface ScreenAsk {
    id: string;
    reason: string;
    state: ScreenAskState;
}

interface QueuedMessage {
    text: string;
    /** what the user attached when pressing send (screens frozen at 🧭 click time, uploaded screenshots); may be empty */
    pending: PendingAttachment[];
    /** bubble text when it differs from what is sent */
    display?: string;
    /** "Report a bug": the reply becomes an editable bug report form */
    bug?: boolean;
    /** CU-14zefhy26bz: send the turn with the `escalate` hint (Retry) and where the message came from */
    escalate?: boolean;
    entryPoint?: string;
    /** Retry: resend the last user message again instead of adding a new bubble */
    retry?: boolean;
    /** CU-14zefhy38pt W3: the Auto / Fast / Thorough switch as it was when the message was sent */
    tier?: TierPreference;
}

interface ChatSession {
    key: string;
    label: string;
    messages: ChatMessage[];
    /** turn currently in flight for THIS session — independent of every other session */
    sending?: boolean;
    /** user messages waiting their turn within THIS session (same-session sends queue, not block) */
    queue?: QueuedMessage[];
    /** poll timer for THIS session's in-flight turn (transient — never restored) */
    pollHandle?: any;
    /** user pressed "Allow for this chat": later screen requests in this chat are answered without asking */
    screenConsent?: boolean;
    /** the app-links hint (`routesHint`) this chat's assistant already got (key of its paths); sent again only when they change */
    routesHint?: string;
    /** epoch ms of the last change to its transcript (server copy vs this browser: the newer one wins) */
    updatedAt?: number;
    /** RMA-10: task of the turn in flight, so polling can resume when the panel is reopened (transient, never restored) */
    taskId?: string;
    /** RMA-27: server revision of the transcript this browser last saw or saved */
    revision?: string;
    /** RMA-27: changed here and not uploaded yet */
    unsynced?: boolean;
    /** CU-14zefhy26bz: a thumbs-down was given: the NEXT turn of this chat goes with `escalate: true` */
    escalateNext?: boolean;
    /** "Did this solve it?" was shown (or answered) in this chat: never asked twice */
    resolutionAsked?: boolean;
    /** epoch ms of the last send / finished turn, for the 10-minute idle question */
    lastActivityAt?: number;
    /** CU-14zefhy38pt W2: pinned by the person (listed first in the history; kept with the transcript) */
    pinned?: boolean;
    /** CU-14zefhy38pt W3: the chat this one was branched from */
    forkedFrom?: string;
    /** CU-14zefhy38pt: the server archived this chat (past the 50 newest unpinned); it still opens and is searchable */
    archived?: boolean;
}

/** A row of the history sidebar: a chat of this browser, or an archived chat that only the server holds (no `session`). */
interface SidebarRow extends HistoryRow {
    session?: ChatSession;
}

/** The window width (1280 when there is no window, e.g. under node). */
const viewportWidth = (): number => (typeof window !== 'undefined' && window.innerWidth ? window.innerWidth : 1280);

/** Order-free key of a routes hint: the current screen's group moves first, which alone is no reason to resend. */
function routesHintKey(hint: string[]): string {
    return hint.join(' ').split(/\s+/).sort().join(' ');
}

const SCREEN_STATE_TEXT: {[k in ScreenAskState]: string} = {
    asking: '',
    sharing: '📷 Capturing your screen…',
    shared: '📷 You shared your screen',
    declined: 'Screen not shared',
    expired: 'Screen request timed out — not shared',
    failed: "📷 Couldn't capture your screen — not shared",
    timedOut: '📷 The screenshot took too long — not shared'
};

/**
 * In-app HaulierMagic Assistant: a chat-bubble icon (sits before the ChatMagic robot icon in the toolbar)
 * that opens a popover chat. Read-only Q&A backed by a Claude Code task in pythonmagic (via the hm
 * `AssistantChatResource`). Each session tab is an independent conversation; transcripts are kept in the
 * browser, while Claude's memory + any uploaded files live server-side in the pythonmagic session.
 */
@Component({
    // Angular 22 defaults to OnPush; Eager preserves the previous behaviour.
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: 'hm-assistant-chat',
    styleUrls: ['./hauliermagic-assistant-chat.component.scss'],
    template: `
        <fa-icon
            *ngIf="claudyEnabled"
            [satPopoverAnchor]="assistantPopover"
            class="hm-assistant-trigger ml-12"
            data-ai-ignore
            [icon]="faCommentDots"
            role="button"
            tabindex="0"
            [attr.aria-label]="'Open ' + label"
            (click)="togglePanel()"
            (keydown.enter)="$event.preventDefault(); togglePanel()"
            (keydown.space)="$event.preventDefault(); togglePanel()"
            nz-tooltip
            [nzTooltipTitle]="label">
        </fa-icon>

        <!-- no popover backdrop: the popover builds its overlay once, so a backdrop could not follow the dock / float switch; the
             floating panel draws its own (below), the docked panel leaves the page usable -->
        <sat-popover horizontalAlign="end" verticalAlign="below" #assistantPopover (opened)="onOpened()" (closed)="onClosed()">
            <!-- one root element (the popover projects a single root; a second one is dropped): the floating panel's click-away layer
                 and the panel -->
            <div class="hm-assistant__root">
                <!-- a floating panel covers the page with a click-away layer, as the popover's own backdrop did -->
                <div
                    class="hm-assistant__backdrop"
                    *ngIf="layout.mode !== 'dock' || layout.full"
                    data-ai-ignore
                    data-testid="assistant-backdrop"
                    [style.--hm-dock-x]="dockX + 'px'"
                    [style.--hm-dock-y]="dockY + 'px'"
                    (click)="assistantPopover.close()"></div>
                <!-- data-ai-ignore: the chat is never in its own screenshots, and clicks in it are not recorded as screen context -->
                <div
                    class="hm-assistant"
                    data-ai-ignore
                    role="dialog"
                    [attr.aria-label]="label"
                    [class.docked]="layout.mode === 'dock'"
                    [class.full]="layout.full"
                    [class.narrow]="narrowPanel"
                    [class.dragging]="dragging"
                    [style.--hm-dock-width]="layout.widthPct + 'vw'"
                    [style.--hm-dock-x]="dockX + 'px'"
                    [style.--hm-dock-y]="dockY + 'px'"
                    (keydown.escape)="onPanelEscape($event)"
                    (dragenter)="onDragOver($event)"
                    (dragover)="onDragOver($event)"
                    (dragleave)="onDragLeave($event)"
                    (drop)="onDrop($event)">
                    <!-- W2: drag the left edge of the docked panel (arrow keys too) to change its width -->
                    <div
                        class="hm-assistant__resize"
                        *ngIf="layout.mode === 'dock' && !layout.full"
                        role="separator"
                        aria-orientation="vertical"
                        [attr.aria-label]="'Resize ' + label"
                        tabindex="0"
                        data-testid="assistant-resize"
                        [attr.aria-valuenow]="layout.widthPct"
                        aria-valuemin="30"
                        aria-valuemax="70"
                        (pointerdown)="startResize($event)"
                        (keydown.arrowleft)="resizeBy(2, $event)"
                        (keydown.arrowright)="resizeBy(-2, $event)"></div>
                    <!-- Header -->
                    <div class="hm-assistant__header">
                        <div class="hm-assistant__brand">
                            <button
                                type="button"
                                class="hm-assistant__btn hm-assistant__header-action hm-assistant__sidebar-toggle"
                                data-testid="assistant-sidebar-toggle"
                                aria-label="Chat history"
                                [attr.aria-expanded]="layout.sidebar"
                                nz-tooltip
                                nzTooltipTitle="Chat history"
                                (click)="toggleSidebar()">
                                <fa-icon [icon]="faBars"></fa-icon>
                                <!-- RMA-48: a screen request waits in a chat while the history is closed -->
                                <span
                                    class="hm-assistant__tab-badge"
                                    *ngIf="!layout.sidebar && anyScreenAsk"
                                    role="status"
                                    title="A chat asks to see your screen"
                                    data-testid="assistant-sidebar-badge"
                                    >!</span
                                >
                            </button>
                            <span class="hm-assistant__avatar"><fa-icon [icon]="faRobot"></fa-icon></span>
                            <div>
                                <div class="hm-assistant__title">{{ label }}</div>
                                <div class="hm-assistant__status"><span class="dot"></span> {{ modeText.status }}</div>
                            </div>
                        </div>
                        <div class="hm-assistant__header-actions">
                            <button
                                type="button"
                                *ngIf="bugReportEnabled"
                                class="hm-assistant__btn hm-assistant__header-action"
                                [class.disabled]="!!activeSession?.sending"
                                [attr.aria-disabled]="!!activeSession?.sending"
                                nz-tooltip
                                nzTooltipTitle="Report a bug"
                                aria-label="Report a bug"
                                data-testid="assistant-report-bug"
                                (click)="reportBug()">
                                <fa-icon [icon]="faBug"></fa-icon>
                            </button>
                            <button
                                type="button"
                                class="hm-assistant__btn hm-assistant__header-action hm-assistant__wide-only"
                                data-testid="assistant-dock-toggle"
                                [attr.aria-label]="layout.mode === 'dock' ? 'Float the panel' : 'Dock the panel at the side'"
                                nz-tooltip
                                [nzTooltipTitle]="layout.mode === 'dock' ? 'Float the panel' : 'Dock the panel at the side'"
                                (click)="toggleDock()">
                                <fa-icon [icon]="layout.mode === 'dock' ? faWindowRestore : faColumns"></fa-icon>
                            </button>
                            <button
                                type="button"
                                class="hm-assistant__btn hm-assistant__header-action hm-assistant__wide-only"
                                data-testid="assistant-full-toggle"
                                [attr.aria-label]="layout.full ? 'Leave full screen' : 'Full screen'"
                                [attr.aria-pressed]="layout.full"
                                nz-tooltip
                                [nzTooltipTitle]="layout.full ? 'Leave full screen' : 'Full screen'"
                                (click)="toggleFull()">
                                <fa-icon [icon]="layout.full ? faCompress : faExpand"></fa-icon>
                            </button>
                            <button
                                type="button"
                                class="hm-assistant__btn hm-assistant__close"
                                [attr.aria-label]="'Close ' + label"
                                data-testid="assistant-close"
                                (click)="assistantPopover.close()">
                                <fa-icon [icon]="faTimes"></fa-icon>
                            </button>
                        </div>
                    </div>

                    <div class="hm-assistant__body">
                        <!-- W2: chat history (replaces the row of "Session N" tabs): titles from the first question, grouped by date,
                         search, rename, pin, delete -->
                        <aside class="hm-assistant__sidebar" *ngIf="layout.sidebar" aria-label="Chat history" data-testid="assistant-sidebar">
                            <button type="button" class="hm-assistant__new" data-testid="assistant-new-session" (click)="newSession()">
                                <fa-icon [icon]="faPlus"></fa-icon> New chat
                            </button>
                            <div class="hm-assistant__search">
                                <fa-icon [icon]="faSearch"></fa-icon>
                                <input
                                    type="search"
                                    maxlength="100"
                                    placeholder="Search chats"
                                    aria-label="Search chats"
                                    data-testid="assistant-history-search"
                                    [ngModel]="historyQuery"
                                    (ngModelChange)="onHistoryQuery($event)" />
                            </div>
                            <label class="hm-assistant__archived-toggle">
                                <input
                                    type="checkbox"
                                    data-testid="assistant-show-archived"
                                    [ngModel]="showArchived"
                                    (ngModelChange)="onShowArchived($event)" />
                                Show archived
                            </label>
                            <div
                                #tabStrip
                                class="hm-assistant__tabs"
                                role="tablist"
                                aria-orientation="vertical"
                                aria-label="Chats"
                                (scroll)="onTabsScroll()">
                                <div class="hm-assistant__empty-list" *ngIf="!historyList.length" role="status">No chats found</div>
                                <ng-container *ngFor="let s of historyList; trackBy: rowKey">
                                    <div class="hm-assistant__group" role="presentation" *ngIf="s.groupStart">{{ s.group }}</div>
                                    <span
                                        class="hm-assistant__tab"
                                        [attr.data-session-key]="s.key"
                                        [class.active]="s.key === activeKey"
                                        [class.attention]="hasWaitingScreenRequest(s.session)">
                                        <ng-container *ngIf="renamingKey !== s.key">
                                            <button
                                                type="button"
                                                role="tab"
                                                class="hm-assistant__btn hm-assistant__tab-label"
                                                data-testid="assistant-tab"
                                                [attr.aria-selected]="s.key === activeKey"
                                                [attr.aria-label]="tabLabel(s)"
                                                [title]="s.label"
                                                (click)="openChat(s)">
                                                <fa-icon *ngIf="s.pinned" class="hm-assistant__pin-mark" [icon]="faThumbtack"></fa-icon>
                                                <fa-icon *ngIf="s.archived" class="hm-assistant__pin-mark" [icon]="faArchive"></fa-icon>
                                                <span class="hm-assistant__tab-text">{{ s.label }}</span>
                                            </button>
                                            <!-- RMA-48: a screen request waiting in a tab that is not the one on show -->
                                            <span
                                                *ngIf="hasWaitingScreenRequest(s.session)"
                                                class="hm-assistant__tab-badge"
                                                role="img"
                                                [attr.aria-label]="'Waiting for your answer: ' + label + ' asks to see your screen'"
                                                [title]="label + ' asks to see your screen'"
                                                data-testid="assistant-tab-badge"
                                                >!</span
                                            >
                                            <span class="hm-assistant__tab-tools" *ngIf="s.session && confirmDeleteKey !== s.key">
                                                <button
                                                    type="button"
                                                    class="hm-assistant__btn"
                                                    data-testid="assistant-pin"
                                                    [attr.aria-label]="(s.pinned ? 'Unpin ' : 'Pin ') + s.label"
                                                    [attr.aria-pressed]="s.pinned"
                                                    (click)="togglePin(s)">
                                                    <fa-icon [icon]="faThumbtack"></fa-icon>
                                                </button>
                                                <button
                                                    type="button"
                                                    class="hm-assistant__btn"
                                                    data-testid="assistant-rename"
                                                    [attr.aria-label]="'Rename ' + s.label"
                                                    (click)="startRename(s)">
                                                    <fa-icon [icon]="faPen"></fa-icon>
                                                </button>
                                                <button
                                                    type="button"
                                                    class="hm-assistant__btn hm-assistant__tab-close"
                                                    data-testid="assistant-delete"
                                                    [attr.aria-label]="'Delete ' + s.label"
                                                    (click)="askDelete(s, $event)">
                                                    <fa-icon [icon]="faTrash"></fa-icon>
                                                </button>
                                            </span>
                                            <span
                                                class="hm-assistant__tab-confirm"
                                                *ngIf="confirmDeleteKey === s.key"
                                                role="group"
                                                aria-label="Delete this chat?">
                                                <button type="button" class="yes" data-testid="assistant-delete-yes" (click)="deleteChat(s, $event)">
                                                    Delete
                                                </button>
                                                <button type="button" class="no" data-testid="assistant-delete-no" (click)="cancelDelete($event)">
                                                    Keep
                                                </button>
                                            </span>
                                        </ng-container>
                                        <input
                                            *ngIf="renamingKey === s.key"
                                            type="text"
                                            class="hm-assistant__rename"
                                            maxlength="100"
                                            aria-label="Chat title"
                                            data-testid="assistant-rename-input"
                                            [(ngModel)]="renameText"
                                            (keydown.enter)="commitRename($event)"
                                            (keydown.escape)="cancelRename($event)"
                                            (blur)="commitRename()" />
                                    </span>
                                </ng-container>
                                <!-- W5: the asking chat is scrolled out of the list: a pinned "!" opens it -->
                                <button
                                    *ngIf="hiddenAsk as ask"
                                    type="button"
                                    class="hm-assistant__tab-ask"
                                    data-testid="assistant-tab-screen-request-hidden"
                                    [attr.aria-label]="'Show ' + displayLabel(ask)"
                                    [title]="displayLabel(ask) + ': ' + label + ' is asking to see your screen'"
                                    (click)="selectSession(ask.key)">
                                    !
                                </button>
                            </div>
                        </aside>

                        <div class="hm-assistant__main">
                            <!-- Uploaded files: the list shows only when the chat has some; drop files anywhere on the panel to add them -->
                            <input #fileInput type="file" multiple hidden aria-label="Attach a file" (change)="onFileSelected($event)" />
                            <div class="hm-assistant__files" *ngIf="visibleFiles.length">
                                <!-- screenshots are attachments of a message (pills), not chat files -->
                                <div class="hm-assistant__file" *ngFor="let f of visibleFiles">
                                    <span class="name">{{ fileName(f) }}</span>
                                    <button
                                        type="button"
                                        class="hm-assistant__btn"
                                        [attr.aria-label]="'Remove file ' + fileName(f)"
                                        (click)="removeFile(f)">
                                        <fa-icon [icon]="faTrash"></fa-icon>
                                    </button>
                                </div>
                            </div>
                            <!-- shown only while files are dragged over the panel -->
                            <div class="hm-assistant__drop" *ngIf="dragging" data-testid="assistant-dropzone" role="status">
                                <fa-icon [icon]="faPaperclip"></fa-icon>
                                <span>Drop files to attach — they stay in this chat for {{ label }} to read</span>
                            </div>

                            <!-- Messages -->
                            <div
                                class="hm-assistant__messages"
                                #scrollArea
                                role="log"
                                aria-live="polite"
                                aria-relevant="additions text"
                                aria-label="Conversation">
                                <!-- CU-14zefhy26bz: first-open notice, dismissible, remembered per user -->
                                <div class="hm-assistant__notice" *ngIf="noticeVisible" role="note" data-testid="assistant-notice">
                                    <span>{{ noticeText }}</span>
                                    <button
                                        type="button"
                                        class="hm-assistant__btn"
                                        aria-label="Dismiss the notice"
                                        data-testid="assistant-notice-dismiss"
                                        (click)="closeNotice()">
                                        <fa-icon [icon]="faTimes"></fa-icon>
                                    </button>
                                </div>

                                <div class="hm-assistant__branch-label" *ngIf="branchLabel as bl" data-testid="assistant-branch-label">{{ bl }}</div>

                                <div class="hm-assistant__intro" *ngIf="activeMessages.length === 0">
                                    Hi 👋 I'm {{ label }}, your assistant. Ask me about your haulier orders, runs, deliveries, invoices or customers —
                                    {{ modeText.intro }}
                                </div>

                                <ng-container *ngFor="let m of activeMessages; let i = index">
                                    <div
                                        class="hm-assistant__msg"
                                        *ngIf="!m.screen && !m.note && !m.editing"
                                        [ngClass]="m.role"
                                        [class.error]="m.error"
                                        [class.stopped]="m.stopped && !m.pending">
                                        <span class="bubble">
                                            <ng-container *ngIf="!m.pending">
                                                <!-- CU-14zefhy21rx X5: replies are markdown (safe subset, bound as text, never as HTML) -->
                                                <div
                                                    class="hm-assistant__md"
                                                    *ngIf="markdownOf(m) as blocks; else plainReply"
                                                    data-testid="assistant-reply-md">
                                                    <ng-container *ngFor="let b of blocks">
                                                        <p *ngIf="b.kind === 'p'" class="md-p">
                                                            <ng-container
                                                                *ngTemplateOutlet="mdInline; context: {$implicit: b.inlines}"></ng-container>
                                                        </p>
                                                        <div
                                                            *ngIf="b.kind === 'heading'"
                                                            class="md-h"
                                                            [ngClass]="'md-h' + b.level"
                                                            role="heading"
                                                            [attr.aria-level]="b.level">
                                                            <ng-container
                                                                *ngTemplateOutlet="mdInline; context: {$implicit: b.inlines}"></ng-container>
                                                        </div>
                                                        <ng-container *ngIf="b.kind === 'list'">
                                                            <ol *ngIf="b.ordered" class="md-list" [attr.start]="b.start">
                                                                <li *ngFor="let it of b.items" [ngClass]="'md-l' + it.level">
                                                                    <ng-container
                                                                        *ngTemplateOutlet="mdInline; context: {$implicit: it.inlines}"></ng-container>
                                                                </li>
                                                            </ol>
                                                            <ul *ngIf="!b.ordered" class="md-list">
                                                                <li *ngFor="let it of b.items" [ngClass]="'md-l' + it.level">
                                                                    <ng-container
                                                                        *ngTemplateOutlet="mdInline; context: {$implicit: it.inlines}"></ng-container>
                                                                </li>
                                                            </ul>
                                                        </ng-container>
                                                        <div *ngIf="b.kind === 'table'" class="md-table-wrap">
                                                            <table class="md-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th *ngFor="let h of b.header; let c = index" [style.text-align]="b.align[c]">
                                                                            <ng-container
                                                                                *ngTemplateOutlet="mdInline; context: {$implicit: h}"></ng-container>
                                                                        </th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    <tr *ngFor="let row of b.rows">
                                                                        <td *ngFor="let cell of row; let c = index" [style.text-align]="b.align[c]">
                                                                            <ng-container
                                                                                *ngTemplateOutlet="
                                                                                    mdInline;
                                                                                    context: {$implicit: cell}
                                                                                "></ng-container>
                                                                        </td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        <div *ngIf="b.kind === 'table'" class="md-table-tools">
                                                            <button type="button" data-testid="assistant-table-csv" (click)="downloadTable(m, b)">
                                                                <fa-icon [icon]="faDownload"></fa-icon> Download CSV
                                                            </button>
                                                        </div>
                                                        <pre *ngIf="b.kind === 'code'" class="md-pre">{{ b.text }}</pre>
                                                        <hr *ngIf="b.kind === 'hr'" class="md-hr" />
                                                    </ng-container>
                                                </div>
                                                <ng-template #plainReply>
                                                    <!-- in-app links in replies ("/runs/12") open the page; everything else is plain text -->
                                                    <ng-container *ngFor="let seg of segmentsOf(m)">
                                                        <a
                                                            *ngIf="seg.link"
                                                            class="hm-assistant__link"
                                                            [href]="seg.link"
                                                            (click)="openLink($event, seg.link)"
                                                            >{{ seg.text }}</a
                                                        >
                                                        <ng-container *ngIf="!seg.link">{{ seg.text }}</ng-container>
                                                    </ng-container>
                                                </ng-template>
                                            </ng-container>
                                            <!-- CU-14zefhy29vq: dots + activity share one flex line, so the label is cut only when the bubble is full -->
                                            <span class="hm-assistant__pending-line" *ngIf="m.pending" data-testid="assistant-pending-line"
                                                ><span class="typing" role="status" [attr.aria-label]="label + ' is working'">
                                                    <span></span><span></span><span></span>
                                                </span>
                                                <!-- RMA-61: what the turn is doing, e.g. "Running a report query · step 3 · 42s" (dots only until the
                                     first activity arrives); the label is announced when it changes, the ticking time is not -->
                                                <!-- CU-14zefhy29vq: label and step are separate spans: only the label is cut (…), the step and the
                                     time stay visible; the full text stays in the title -->
                                                <span class="hm-assistant__activity" *ngIf="m.pending && m.activity" data-testid="assistant-activity"
                                                    ><span
                                                        class="hm-assistant__activity-status"
                                                        role="status"
                                                        aria-live="polite"
                                                        data-testid="assistant-activity-status"
                                                        [title]="activityTextOf(m)"
                                                        ><span class="hm-assistant__activity-text" data-testid="assistant-activity-label">{{
                                                            activityLabelOf(m)
                                                        }}</span
                                                        ><span class="hm-assistant__activity-step" data-testid="assistant-activity-step">{{
                                                            activityStepOf(m)
                                                        }}</span></span
                                                    ><span
                                                        class="hm-assistant__activity-time"
                                                        aria-hidden="true"
                                                        data-testid="assistant-activity-time"
                                                        >{{ activityElapsedOf(m) }}</span
                                                    ></span
                                                ></span
                                            >
                                            <!-- while the turn runs: "Still working…", the reconnecting note or the assistant's latest narrative -->
                                            <span class="hm-assistant__live" *ngIf="liveStatusText(m) as live" data-testid="assistant-live-status">{{
                                                live
                                            }}</span>
                                            <!-- CU-14zefhy26bz: stop the running turn -->
                                            <button
                                                type="button"
                                                class="hm-assistant__stop"
                                                *ngIf="m.pending && m.taskId"
                                                [disabled]="m.stopped"
                                                data-testid="assistant-stop"
                                                (click)="stopTurn(m)">
                                                <fa-icon [icon]="faStop"></fa-icon> {{ m.stopped ? 'Stopping…' : 'Stop' }}
                                            </button>
                                            <!-- what went with this message: 🧭 screen contexts and 📷 screenshots -->
                                            <span
                                                class="hm-assistant__sent-pills"
                                                *ngIf="m.attachments?.length"
                                                data-testid="assistant-sent-attachments">
                                                <span class="hm-assistant__pill" *ngFor="let a of m.attachments" [ngClass]="a.kind" [title]="a.label">
                                                    <fa-icon [icon]="a.kind === 'context' ? faCrosshairs : faCamera"></fa-icon>
                                                    <span class="hm-assistant__pill-label">{{ a.label }}</span>
                                                </span>
                                            </span>
                                        </span>
                                    </div>

                                    <!-- W3: a finished question can start a branch or be edited and sent again -->
                                    <div
                                        class="hm-assistant__user-actions"
                                        *ngIf="m.role === 'user' && !m.editing && canBranchHere(m)"
                                        data-testid="assistant-user-actions">
                                        <button *ngIf="canEdit(m)" type="button" data-testid="assistant-edit" (click)="startEdit(m)">
                                            <fa-icon [icon]="faPen"></fa-icon> Edit and resend
                                        </button>
                                        <button type="button" data-testid="assistant-branch" (click)="branchFrom(m)">
                                            <fa-icon [icon]="faCodeBranch"></fa-icon> Branch from here
                                        </button>
                                    </div>
                                    <div class="hm-assistant__edit" *ngIf="m.role === 'user' && m.editing" data-testid="assistant-edit-box">
                                        <textarea
                                            rows="3"
                                            maxlength="4000"
                                            aria-label="Edit your question"
                                            data-testid="assistant-edit-text"
                                            [(ngModel)]="m.editText"
                                            (keydown.control.enter)="resendEdited(m)"></textarea>
                                        <div class="hm-assistant__edit-actions">
                                            <button
                                                type="button"
                                                class="send"
                                                data-testid="assistant-edit-send"
                                                [disabled]="!(m.editText || '').trim() || forking"
                                                (click)="resendEdited(m)">
                                                Send in a new branch
                                            </button>
                                            <button type="button" class="skip" data-testid="assistant-edit-cancel" (click)="cancelEdit(m)">
                                                Cancel
                                            </button>
                                        </div>
                                    </div>

                                    <!-- CU-14zefhy26bz: thumbs, copy and retry under the reply (shown on hover / for the last reply) -->
                                    <div
                                        class="hm-assistant__actions"
                                        *ngIf="showActions(m) || retryAvailable(i)"
                                        [class.last]="i === activeMessages.length - 1"
                                        [class.chosen]="!!m.feedback?.thumb"
                                        data-testid="assistant-reply-actions">
                                        <ng-container *ngIf="showActions(m)">
                                            <button
                                                type="button"
                                                class="hm-assistant__act"
                                                *ngIf="canRate(m)"
                                                [class.on]="m.feedback?.thumb === '+1'"
                                                [attr.aria-pressed]="m.feedback?.thumb === '+1'"
                                                aria-label="Good answer"
                                                title="Good answer"
                                                data-testid="assistant-thumb-up"
                                                (click)="rate(m, '+1')">
                                                <fa-icon [icon]="faThumbsUp"></fa-icon>
                                            </button>
                                            <button
                                                type="button"
                                                class="hm-assistant__act"
                                                *ngIf="canRate(m)"
                                                [class.on]="m.feedback?.thumb === '-1'"
                                                [attr.aria-pressed]="m.feedback?.thumb === '-1'"
                                                aria-label="Bad answer"
                                                title="Bad answer"
                                                data-testid="assistant-thumb-down"
                                                (click)="rate(m, '-1')">
                                                <fa-icon [icon]="faThumbsDown"></fa-icon>
                                            </button>
                                            <button
                                                type="button"
                                                class="hm-assistant__act"
                                                [attr.aria-label]="m.copied ? 'Copied' : 'Copy the reply'"
                                                [title]="m.copied ? 'Copied' : 'Copy the reply'"
                                                data-testid="assistant-copy"
                                                (click)="copyReply(m)">
                                                <fa-icon [icon]="m.copied ? faCheck : faCopy"></fa-icon>
                                            </button>
                                            <button
                                                type="button"
                                                class="hm-assistant__act"
                                                aria-label="Branch from here"
                                                title="Branch from here"
                                                data-testid="assistant-branch"
                                                [disabled]="forking"
                                                (click)="branchFrom(m)">
                                                <fa-icon [icon]="faCodeBranch"></fa-icon>
                                            </button>
                                        </ng-container>
                                        <button
                                            type="button"
                                            class="hm-assistant__act retry"
                                            *ngIf="retryAvailable(i)"
                                            data-testid="assistant-retry"
                                            (click)="retry(m, i)">
                                            <fa-icon [icon]="faRedo"></fa-icon> Retry
                                        </button>
                                    </div>
                                    <div
                                        class="hm-assistant__reasons"
                                        *ngIf="m.feedback?.open"
                                        role="group"
                                        aria-label="What went wrong?"
                                        data-testid="assistant-reasons">
                                        <span class="q">What went wrong? <small>(optional)</small></span>
                                        <div class="chips">
                                            <button
                                                type="button"
                                                class="hm-assistant__reason"
                                                *ngFor="let r of reasonOptions"
                                                [class.on]="reasonOn(m, r.code)"
                                                [attr.aria-pressed]="reasonOn(m, r.code)"
                                                data-testid="assistant-reason"
                                                (click)="pickReason(m, r.code)">
                                                {{ r.label }}
                                            </button>
                                        </div>
                                        <div class="detail">
                                            <input
                                                type="text"
                                                maxlength="500"
                                                placeholder="Tell us more (optional)"
                                                aria-label="Tell us more"
                                                data-testid="assistant-reason-comment"
                                                [(ngModel)]="m.feedback.comment"
                                                (keydown.enter)="sendReasons(m)" />
                                            <button
                                                type="button"
                                                class="hm-assistant__act send"
                                                data-testid="assistant-reason-send"
                                                (click)="sendReasons(m)">
                                                Send
                                            </button>
                                            <button
                                                type="button"
                                                class="hm-assistant__act"
                                                aria-label="Skip"
                                                data-testid="assistant-reason-skip"
                                                (click)="closeReasons(m)">
                                                <fa-icon [icon]="faTimes"></fa-icon>
                                            </button>
                                        </div>
                                    </div>

                                    <!-- W3: which model answered -->
                                    <div
                                        class="hm-assistant__answered-by"
                                        *ngIf="!m.pending && m.role === 'assistant' && answeredByText(m)"
                                        data-testid="assistant-answered-by">
                                        {{ answeredByText(m) }}
                                    </div>

                                    <!-- W4 (C3): one question looks at up to N days; this says which period the answer covers and offers the full history -->
                                    <div class="hm-assistant__window" *ngIf="m.dataWindow && !m.pending" data-testid="assistant-data-window">
                                        <span class="hm-assistant__window-text"
                                            >Answered for {{ m.dataWindow.from }} to {{ m.dataWindow.to }} (up to {{ m.dataWindow.days }} days per
                                            question).</span
                                        >
                                        <button
                                            *ngIf="historyRequestEnabled && m.dataWindow.requestable && !m.historyAsk"
                                            type="button"
                                            class="hm-assistant__window-ask"
                                            data-testid="assistant-request-history"
                                            (click)="requestHistory(m)">
                                            Request full history
                                        </button>
                                        <span *ngIf="m.historyAsk?.state === 'sending'" role="status">Sending your request…</span>
                                        <span *ngIf="m.historyAsk?.state === 'sent'" role="status" data-testid="assistant-history-sent">
                                            Requested:
                                            <a *ngIf="m.historyAsk.taskUrl" [href]="m.historyAsk.taskUrl" target="_blank" rel="noopener noreferrer">{{
                                                m.historyAsk.taskUrl
                                            }}</a>
                                            <ng-container *ngIf="!m.historyAsk.taskUrl">the team has your request.</ng-container>
                                        </span>
                                        <span *ngIf="m.historyAsk?.state === 'failed'" class="hm-assistant__window-error" role="alert">
                                            {{ m.historyAsk.error }}
                                            <button type="button" class="hm-assistant__window-ask" (click)="requestHistory(m)">Try again</button>
                                        </span>
                                    </div>

                                    <!-- F3: files the assistant produced for this reply; download is an authenticated request -->
                                    <div class="hm-assistant__outputs" *ngIf="m.outputFiles?.length" data-testid="assistant-output-files">
                                        <button
                                            type="button"
                                            class="hm-assistant__chip"
                                            *ngFor="let c of m.outputFiles"
                                            [title]="'Download ' + c.name"
                                            [disabled]="downloading.has(c.path)"
                                            data-testid="assistant-output-chip"
                                            (click)="downloadOutput(c)">
                                            <fa-icon [icon]="faDownload"></fa-icon>
                                            <span class="chip-name">{{ c.name }}</span>
                                            <span class="chip-size" *ngIf="sizeLabel(c.bytes)">{{ sizeLabel(c.bytes) }}</span>
                                        </button>
                                    </div>

                                    <div class="hm-assistant__note" *ngIf="m.note" data-testid="assistant-note">{{ m.text }}</div>

                                    <!-- The assistant asks to see the screen: nothing is captured until the user allows it -->
                                    <div
                                        class="hm-assistant__screen"
                                        *ngIf="m.screen"
                                        [ngClass]="m.screen.state"
                                        data-testid="assistant-screen-request">
                                        <ng-container *ngIf="m.screen.state === 'asking'">
                                            <div class="hm-assistant__screen-title">📷 {{ label }} wants to see your screen</div>
                                            <div class="hm-assistant__screen-reason" *ngIf="m.screen.reason">{{ m.screen.reason }}</div>
                                            <div class="hm-assistant__screen-hint">
                                                The chat window and password fields are left out of the picture.
                                            </div>
                                            <div class="hm-assistant__plan-actions">
                                                <button type="button" class="approve" data-testid="assistant-screen-allow" (click)="allowScreen(m)">
                                                    Allow for this chat
                                                </button>
                                                <button
                                                    type="button"
                                                    class="cancel"
                                                    data-testid="assistant-screen-decline"
                                                    (click)="declineScreen(m)">
                                                    Not now
                                                </button>
                                            </div>
                                        </ng-container>
                                        <div class="hm-assistant__screen-state" *ngIf="m.screen.state !== 'asking'">
                                            {{ screenStateText(m.screen) }}
                                        </div>
                                    </div>

                                    <!-- Proposed change: nothing is written until the user approves -->
                                    <div class="hm-assistant__plan" *ngIf="m.plan" [ngClass]="m.plan.state" data-testid="assistant-plan-card">
                                        <div class="hm-assistant__plan-title">Proposed change</div>
                                        <div class="hm-assistant__plan-summary" *ngIf="m.plan.summary">{{ m.plan.summary }}</div>
                                        <ol class="hm-assistant__plan-steps" *ngIf="m.plan.steps.length">
                                            <li *ngFor="let step of m.plan.steps">{{ step }}</li>
                                        </ol>
                                        <div class="hm-assistant__plan-actions" *ngIf="actionsVisible(editEnabled, planLabel(m.plan))">
                                            <button
                                                type="button"
                                                class="approve"
                                                data-testid="assistant-plan-approve"
                                                [disabled]="!canAct(m.plan)"
                                                (click)="approvePlan(m)">
                                                Approve
                                            </button>
                                            <button
                                                type="button"
                                                class="cancel"
                                                data-testid="assistant-plan-cancel"
                                                [disabled]="!canAct(m.plan)"
                                                (click)="cancelPlan(m)">
                                                Cancel
                                            </button>
                                        </div>
                                        <div class="hm-assistant__plan-state" *ngIf="planLabel(m.plan)">{{ planLabel(m.plan) }}</div>
                                    </div>

                                    <!-- What an approved change did, with Undo (the revert is a new plan the user approves) -->
                                    <div class="hm-assistant__changes" *ngIf="m.changes" [ngClass]="m.changes.undo" data-testid="assistant-changes">
                                        <div class="hm-assistant__plan-title">What changed</div>
                                        <ul class="hm-assistant__changes-list">
                                            <li *ngFor="let line of changeLinesOf(m)">{{ line }}</li>
                                        </ul>
                                        <div class="hm-assistant__plan-actions" *ngIf="actionsVisible(editEnabled, undoLabel(m.changes))">
                                            <button
                                                type="button"
                                                class="cancel"
                                                data-testid="assistant-undo"
                                                [disabled]="!canUndoChanges(m.changes)"
                                                (click)="undoChanges(m)">
                                                <fa-icon [icon]="faUndo"></fa-icon> Undo
                                            </button>
                                        </div>
                                        <div class="hm-assistant__plan-state" *ngIf="undoLabel(m.changes)">{{ undoLabel(m.changes) }}</div>
                                        <div class="hm-assistant__plan-state" *ngIf="!undoLabel(m.changes) && m.changes.note">
                                            {{ m.changes.note }}
                                        </div>
                                    </div>

                                    <!-- Drafted bug report: the user edits it; nothing is filed until Submit -->
                                    <div
                                        class="hm-assistant__bug"
                                        *ngIf="m.bug && m.bug.state !== 'dismissed'"
                                        [ngClass]="m.bug.state"
                                        data-testid="assistant-bug-card">
                                        <div class="hm-assistant__plan-title">🐞 Bug report</div>
                                        <ng-container *ngIf="m.bug.state !== 'submitted'">
                                            <label>Title</label>
                                            <input
                                                type="text"
                                                maxlength="200"
                                                data-testid="assistant-bug-title"
                                                [disabled]="m.bug.state === 'submitting'"
                                                [(ngModel)]="m.bug.draft.title"
                                                (change)="persist()" />
                                            <label>Steps to reproduce</label>
                                            <textarea
                                                rows="3"
                                                data-testid="assistant-bug-steps"
                                                [disabled]="m.bug.state === 'submitting'"
                                                [(ngModel)]="m.bug.draft.steps"
                                                (change)="persist()"></textarea>
                                            <label>Expected</label>
                                            <textarea
                                                rows="2"
                                                [disabled]="m.bug.state === 'submitting'"
                                                [(ngModel)]="m.bug.draft.expected"
                                                (change)="persist()"></textarea>
                                            <label>Actual</label>
                                            <textarea
                                                rows="2"
                                                [disabled]="m.bug.state === 'submitting'"
                                                [(ngModel)]="m.bug.draft.actual"
                                                (change)="persist()"></textarea>
                                            <div class="hm-assistant__bug-hint">{{ bugIncludes(m.bug) }}</div>
                                            <div class="hm-assistant__bug-error" *ngIf="m.bug.error">{{ m.bug.error }}</div>
                                            <div class="hm-assistant__plan-actions">
                                                <button
                                                    type="button"
                                                    class="approve"
                                                    data-testid="assistant-bug-submit"
                                                    [disabled]="!canSubmit(m.bug)"
                                                    (click)="submitBug(m)">
                                                    {{ m.bug.state === 'submitting' ? 'Sending…' : 'Submit' }}
                                                </button>
                                                <button
                                                    type="button"
                                                    class="cancel"
                                                    [disabled]="m.bug.state === 'submitting'"
                                                    (click)="dismissBug(m)">
                                                    Discard
                                                </button>
                                            </div>
                                        </ng-container>
                                        <div
                                            class="hm-assistant__plan-state"
                                            *ngIf="m.bug.state === 'submitted'"
                                            data-testid="assistant-bug-reported">
                                            Reported:
                                            <a *ngIf="m.bug.taskUrl" [href]="m.bug.taskUrl" target="_blank" rel="noopener noreferrer">{{
                                                m.bug.taskUrl
                                            }}</a>
                                            <span *ngIf="!m.bug.taskUrl">thank you</span>
                                            <div class="hm-assistant__bug-hint" *ngIf="m.bug.warning">{{ m.bug.warning }}</div>
                                        </div>
                                    </div>

                                    <!-- after a reply about an error: offer to report it -->
                                    <div class="hm-assistant__report-link" *ngIf="offerBugReport(m, i)">
                                        <a href="#" data-testid="assistant-report-bug-link" (click)="$event.preventDefault(); reportBug()"
                                            >🐞 Report a bug</a
                                        >
                                    </div>
                                </ng-container>

                                <!-- F3: produced files no reply claims (a chat opened from an older save): listed once under the last reply -->
                                <div class="hm-assistant__outputs" *ngIf="unclaimedOutputFiles.length" data-testid="assistant-output-files-more">
                                    <button
                                        type="button"
                                        class="hm-assistant__chip"
                                        *ngFor="let c of unclaimedOutputFiles"
                                        [title]="'Download ' + c.name"
                                        [disabled]="downloading.has(c.path)"
                                        data-testid="assistant-output-chip"
                                        (click)="downloadOutput(c)">
                                        <fa-icon [icon]="faDownload"></fa-icon>
                                        <span class="chip-name">{{ c.name }}</span>
                                        <span class="chip-size" *ngIf="sizeLabel(c.bytes)">{{ sizeLabel(c.bytes) }}</span>
                                    </button>
                                </div>
                            </div>

                            <!-- CU-14zefhy26bz: "Did this solve it?" once per chat, one line, dismissable, never while a turn runs -->
                            <div
                                class="hm-assistant__resolution"
                                *ngIf="resolutionAsk && !activeSession?.sending"
                                role="group"
                                aria-label="Did this solve it?"
                                data-testid="assistant-resolution">
                                <ng-container *ngIf="!resolutionAsk.answered">
                                    <span class="q">Did this solve it?</span>
                                    <button type="button" data-testid="assistant-resolution-yes" (click)="answerResolution('yes')">Yes</button>
                                    <button type="button" data-testid="assistant-resolution-partly" (click)="answerResolution('partly')">
                                        Partly
                                    </button>
                                    <button type="button" data-testid="assistant-resolution-no" (click)="answerResolution('no')">No</button>
                                    <button
                                        type="button"
                                        class="dismiss"
                                        aria-label="Dismiss"
                                        data-testid="assistant-resolution-dismiss"
                                        (click)="dismissResolution()">
                                        <fa-icon [icon]="faTimes"></fa-icon>
                                    </button>
                                </ng-container>
                                <span class="q" *ngIf="resolutionAsk.answered" data-testid="assistant-resolution-thanks"
                                    >Thanks for the feedback.</span
                                >
                            </div>

                            <!-- Suggested prompts -->
                            <div class="hm-assistant__suggestions" *ngIf="shownSuggestions.length">
                                <button *ngFor="let p of shownSuggestions" (click)="useSuggestion(p)">{{ p }}</button>
                            </div>

                            <!-- Attached to the next message (nothing about the screen is sent unless attached here); × removes one -->
                            <div
                                class="hm-assistant__pending"
                                *ngIf="pendingAttachments.length || attachHint || capturing"
                                data-testid="assistant-pending-attachments">
                                <span
                                    class="hm-assistant__pill"
                                    *ngFor="let a of pendingAttachments"
                                    [ngClass]="a.kind"
                                    [title]="pillTitle(a)"
                                    data-testid="assistant-pending-pill">
                                    <fa-icon [icon]="a.kind === 'context' ? faCrosshairs : faCamera"></fa-icon>
                                    <span class="hm-assistant__pill-label">{{ a.label }}</span>
                                    <button
                                        type="button"
                                        class="hm-assistant__btn remove"
                                        title="Remove"
                                        [attr.aria-label]="'Remove ' + a.label"
                                        data-testid="assistant-pending-remove"
                                        (click)="removePending(a)">
                                        <fa-icon [icon]="faTimes"></fa-icon>
                                    </button>
                                </span>
                                <span class="hm-assistant__pending-hint" *ngIf="capturing" data-testid="assistant-capturing">📷 Capturing…</span>
                                <span class="hm-assistant__pending-hint" *ngIf="attachHint">{{ attachHint }}</span>
                            </div>

                            <!-- W3 (C5): which model answers -->
                            <div class="hm-assistant__tier" role="radiogroup" aria-label="Answer mode" data-testid="assistant-tier">
                                <span class="hm-assistant__tier-label" aria-hidden="true">Answer:</span>
                                <button
                                    *ngFor="let o of tierOptions"
                                    type="button"
                                    role="radio"
                                    class="tier-option"
                                    [class.on]="shownTier === o.value"
                                    [attr.aria-checked]="shownTier === o.value"
                                    [attr.data-tier]="o.value"
                                    [disabled]="!o.enabled"
                                    [title]="o.hint"
                                    (click)="pickTier(o)">
                                    {{ o.label }}
                                </button>
                            </div>

                            <!-- Input -->
                            <div class="hm-assistant__input">
                                <button type="button" class="hm-assistant__btn attach" aria-label="Attach a file" (click)="fileInput.click()">
                                    <fa-icon [icon]="faPaperclip"></fa-icon>
                                </button>
                                <button
                                    type="button"
                                    class="hm-assistant__btn attach camera"
                                    [class.disabled]="capturing"
                                    [class.full]="!canAddShot"
                                    [attr.aria-disabled]="capturing || !canAddShot"
                                    nz-tooltip
                                    [nzTooltipTitle]="canAddShot ? 'Attach a screenshot of your screen to the next message' : shotsFullText"
                                    aria-label="Attach a screenshot of your screen"
                                    data-testid="assistant-screenshot"
                                    (click)="attachScreenshot()">
                                    <fa-icon [icon]="faCamera"></fa-icon>
                                </button>
                                <button
                                    type="button"
                                    class="hm-assistant__btn attach context"
                                    [class.full]="!canAddCtx"
                                    [attr.aria-disabled]="!canAddCtx"
                                    nz-tooltip
                                    [nzTooltipTitle]="canAddCtx ? 'Attach current screen context' : contextsFullText"
                                    aria-label="Attach current screen context"
                                    data-testid="assistant-attach-context"
                                    (click)="attachContext()">
                                    <fa-icon [icon]="faCrosshairs"></fa-icon>
                                </button>
                                <textarea
                                    #inputBox
                                    rows="1"
                                    placeholder="Ask about orders, runs, invoices…"
                                    [attr.aria-label]="'Message to ' + label"
                                    [(ngModel)]="draft"
                                    (focus)="refreshContext()"
                                    (input)="onBoxInput()"
                                    (paste)="onPaste($event)"
                                    (keydown)="onKeydown($event)"></textarea>
                                <button
                                    *ngIf="voiceSupported"
                                    type="button"
                                    class="hm-assistant__btn attach mic"
                                    data-testid="assistant-voice"
                                    [class.listening]="listening"
                                    [attr.aria-label]="listening ? 'Stop dictation' : 'Dictate your question'"
                                    [attr.aria-pressed]="listening"
                                    nz-tooltip
                                    [nzTooltipTitle]="listening ? 'Stop dictation' : 'Dictate your question with your browser'"
                                    (click)="toggleVoice()">
                                    <fa-icon [icon]="faMicrophone"></fa-icon>
                                </button>
                                <button
                                    type="button"
                                    class="hm-assistant__btn send"
                                    [class.disabled]="!draft.trim()"
                                    [attr.aria-disabled]="!draft.trim()"
                                    aria-label="Send message"
                                    data-testid="assistant-send"
                                    (click)="send()">
                                    <fa-icon [icon]="faPaperPlane"></fa-icon>
                                </button>
                            </div>
                            <div class="hm-assistant__voice-error" *ngIf="voiceError" role="alert" data-testid="assistant-voice-error">
                                {{ voiceError }}
                            </div>
                            <div class="hm-assistant__footer">{{ label }} may be inaccurate — verify before acting.</div>
                        </div>
                    </div>
                </div>
            </div>
        </sat-popover>

        <!-- CU-14zefhy21rx X5: one line of reply markdown (bold / italics / code / in-app links), bound as text only -->
        <ng-template #mdInline let-runs>
            <ng-container *ngFor="let r of runs">
                <a
                    *ngIf="r.link"
                    class="hm-assistant__link"
                    [class.md-b]="r.bold"
                    [class.md-i]="r.italic"
                    [class.md-code]="r.code"
                    [href]="r.link"
                    (click)="openLink($event, r.link)"
                    >{{ r.text }}</a
                >
                <code *ngIf="!r.link && r.code" class="md-code" [class.md-b]="r.bold" [class.md-i]="r.italic">{{ r.text }}</code>
                <strong *ngIf="!r.link && !r.code && r.bold" [class.md-i]="r.italic">{{ r.text }}</strong>
                <em *ngIf="!r.link && !r.code && !r.bold && r.italic">{{ r.text }}</em>
                <ng-container *ngIf="!r.link && !r.code && !r.bold && !r.italic">{{ r.text }}</ng-container>
            </ng-container>
        </ng-template>
    `,
    standalone: false
})
export class HauliermagicAssistantChatComponent implements OnDestroy, AfterViewInit, DoCheck {
    @ViewChild('assistantPopover') assistantPopover: SatPopoverComponent;
    @ViewChild('scrollArea') scrollArea: ElementRef;
    @ViewChild('inputBox') inputBox: ElementRef;
    private tabStripRef: ElementRef | undefined;
    /** W5: the chat list lives in the popover and in the sidebar (shown and hidden), so the resize observer follows the element, not the first view init. */
    @ViewChild('tabStrip')
    set tabStrip(ref: ElementRef | undefined) {
        this.tabStripRef = ref;
        this.stripWatcher.attach(ref?.nativeElement);
    }
    get tabStrip(): ElementRef | undefined {
        return this.tabStripRef;
    }

    faCommentDots = faCommentDots;
    faRobot = faRobot;
    faTimes = faTimes;
    faPlus = faPlus;
    faPaperclip = faPaperclip;
    faPaperPlane = faPaperPlane;
    faTrash = faTrash;
    faCamera = faCamera;
    faCrosshairs = faCrosshairs;
    faBug = faBug;
    faUndo = faUndo;
    actionsVisible = actionsVisible;
    faDownload = faDownload;
    faCheck = faCheck;
    faCopy = faCopy;
    faRedo = faRedo;
    faStop = faStop;
    faThumbsUp = faThumbsUp;
    faThumbsDown = faThumbsDown;
    faBars = faBars;
    faCodeBranch = faCodeBranch;
    faColumns = faColumns;
    faCompress = faCompress;
    faExpand = faExpand;
    faMicrophone = faMicrophone;
    faPen = faPen;
    faSearch = faSearch;
    faThumbtack = faThumbtack;
    faArchive = faArchive;
    faWindowRestore = faWindowRestore;
    /** W1 (C8): the one product name every text of the panel uses */
    readonly label = ASSISTANT_LABEL;
    /** W2: where the panel sits (docked / floating), its width and the history sidebar; remembered per person */
    layout: PanelLayout = readLayout(null, viewportWidth());
    /** W2: the search box of the history sidebar, the chat being renamed / asked to be deleted ('' = none) */
    historyQuery = '';
    /** CU-14zefhy38pt: "Show archived", and the archived / found chats only the server holds */
    showArchived = false;
    serverRows: ServerSessionInfo[] = [];
    private searchSeq = 0;
    private searchTimer: any;
    renamingKey = '';
    renameText = '';
    confirmDeleteKey = '';
    /** W2: files are being dragged over the panel */
    dragging = false;
    /**
     * Where the popover's animated container sits (it has a transform, so a fixed panel inside it is placed from that corner, not
     * from the window): the docked / full-screen panel subtracts it to reach the window edge.
     */
    dockX = 0;
    dockY = 0;
    private relayout = false;
    private resizeCleanup: (() => void) | null = null;
    /** W4: "request full history" tasks are configured on the server (`historyRequest` of the chat state) */
    historyRequestEnabled = false;
    /** W3: the browser can recognise speech (the mic button is hidden otherwise); dictation state and its error */
    readonly voiceSupported = typeof window !== 'undefined' && isVoiceSupported(window);
    listening = false;
    voiceError = '';
    private dictation: AssistantDictation | null = null;
    private dictationIgnore = false;
    private voiceToken = 0;
    /** W3: the Auto / Fast / Thorough switch (remembered per person) */
    tier: TierPreference = 'auto';
    /** W3: a branch is being made */
    forking = false;
    readonly reasonOptions = FEEDBACK_REASONS;
    readonly noticeText = NOTICE_TEXT;
    /** the first-open notice is on show (until the user dismisses it; remembered per user in the browser) */
    noticeVisible = false;
    private noticeStoreKey: string | null = null;
    /** "Did this solve it?" card: the chat it asks about (it may be a tab that was just closed) */
    resolutionAsk: {session: string; answered?: boolean} | null = null;
    private resolutionTimer: any;
    /** where the draft came from (a suggestion or an error toast) while it is still unchanged */
    private draftSource: EntryPoint | undefined;
    private draftSourceText = '';
    private readonly uiEvents = new UiEventBuffer();
    private eventTimer: any;
    private panelOpenedAt = 0;
    private suggestionsShownKey = '';
    readonly sizeLabel = fileSizeLabel;
    /** paths being downloaded now (their chip is disabled) */
    readonly downloading = new Set<string>();
    /** F3: the outputs present before each turn, to tell which files the turn produced (a promise: the list call may still run) */
    private readonly outputBaselines = new WeakMap<ChatMessage, Promise<Map<string, string> | null>>();
    /** the live line under the typing dots while a turn runs (X2: "Still working…" / reconnecting / narrative) */
    readonly liveStatusText = liveStatusText;
    /** RMA-61: "now" of the activity timers, bumped every second while a turn with a known activity runs */
    activityNow = Date.now();
    private activityTimer: any = null;

    /** "Report a bug" is configured on the server (chat state `bugReport`); off = no bug action anywhere */
    bugReportEnabled = false;

    readonly contextsFullText = `Up to ${MAX_CONTEXTS} screen contexts per message`;
    readonly shotsFullText = `Up to ${MAX_SCREENSHOTS} screenshots per message`;

    sessions: ChatSession[] = [];
    activeKey = '';
    files: any[] = [];
    draft = '';

    /** Claudy (`claudy-feature`) — DISABLED by default; only shows when explicitly enabled on the role/profile. */
    claudyEnabled = false;

    /**
     * Plan → Approve is on for this tenant: set from the server's chat state (`planMode`) or once a plan arrives.
     * Off by default so tenants without MCPMagic keep today's read-only wording.
     */
    planMode = false;
    modeText = assistantModeText(false);
    /** W3: false when the server says this login may not create / edit (`capabilities.edit`): no Approve, Cancel or Undo. */
    editEnabled = true;
    private planModeChecked = false;

    suggestions = ['Summarise my open haulier orders', 'Why might a run be delayed?', 'How is an invoice total calculated?'];

    /** prompts that fit the current screen ("Why can't I save?", "Summarise this run", …); worked out locally, never sent */
    contextPrompts: string[] = [];
    /** a screenshot is being taken / uploaded */
    capturing = false;
    /** short feedback under the composer ("This screen is already attached") */
    attachHint = '';
    private attachHintTimer: any;

    private roleSub: Subscription;
    private routerSub: Subscription;
    private askSub: Subscription;
    private segmentCache = new WeakMap<ChatMessage, {text: string; version: number; segments: TextSegment[]}>();
    private markdownCache = new WeakMap<ChatMessage, {text: string; version: number; blocks: MdBlock[] | null}>();
    /** top-level app routes ("orders", "runs", ...): only bare `/path` words under these become links */
    private routeRoots: string[];
    /** bumped when a lazy module's routes load, so links into it are checked again */
    private routesVersion = 0;
    /** what the "Report a bug" drafting turn sent (frozen contexts, screenshots), keyed by its reply placeholder */
    /** the screens / screenshots a turn went out with, so Retry sends them again (a turn that never started gives them back to the composer) */
    private turnPending = new WeakMap<ChatMessage, PendingAttachment[]>();
    private bugTurns = new WeakMap<ChatMessage, {contexts?: any[]; screenshots?: string[]}>();
    /** approved revert turns (`undo.<taskId>` plan id), keyed by their reply placeholder: the original card settles when they end */
    private undoTurns = new WeakMap<ChatMessage, string>();

    // server-side history (transcript.json per session): what changed since the last sync, and what to upload
    /** per session: fingerprint of its stored transcript content as last seen here (a change bumps `updatedAt`) */
    private historyPrints = new Map<string, string>();
    private historyDirty = new Set<string>();
    private historyTimer: any;
    private historyChecked = false;
    /** false when the server has no history endpoints (older API): stop calling them for this page load */
    private historyAvailable = true;
    /** sessions the user closed here: a server copy must not bring them back */
    private closedKeys: string[] = [];
    /** RMA-12: browser-storage key of the signed-in tenant + login (null until the identity is known: nothing is read or written) */
    private storageKey: string | null = null;
    /** bumped whenever the chat is bound to another user / reset by logout: results of older async work are dropped */
    private epoch = 0;
    private logoutSub: Subscription;
    /** W5: the asking tab scrolled out of the strip, measured on scroll / resize / tab change, not on every change detection. */
    hiddenAsk: ChatSession | null = null;
    private askRecheck = createAskRecheck(() => this.refreshHiddenAsk());
    /** W5: the strip resizing or appearing (window, panel, sidebar) can hide or show the asking tab. */
    private stripWatcher = createStripWatcher<HTMLElement>((el) => {
        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(() => this.refreshHiddenAsk());
        observer.observe(el);
        return observer;
    });
    private revealTimer: ReturnType<typeof setTimeout> | undefined;

    constructor(
        private assistantService: AssistantService,
        private roleService: RoleService,
        private assistantContext: AssistantContextService,
        private router: Router,
        private routePreloader: AssistantRoutePreloader,
        private attachments: AssistantAttachmentsService,
        private principal: Principal,
        private translate: TranslateService,
        private cdr: ChangeDetectorRef
    ) {
        // RMA-61: EN texts of the activity hint (merged into the app's translations; other languages fall back to the server label)
        this.translate.setTranslation('en', ACTIVITY_TRANSLATIONS_EN, true);
        this.restore();
        // RMA-12: logout empties the in-memory chats (the login service already cleared the stored ones)
        this.logoutSub = assistantLoggedOut$.subscribe(() => this.unbind());
        // Claudy is opt-in: only on when explicitly set to true on the current role's settings.
        this.roleSub = this.roleService.roleSettings.subscribe((roleSettings) => {
            this.claudyEnabled = !!roleSettings && roleSettings['claudy-feature'] === true;
            // error toasts / dialogs offer "Ask CoPilot" only while the CoPilot is on for this user
            this.attachments.copilotEnabled = this.claudyEnabled;
            // read the chat state once now (not only on first open) so the tenant's date format is known before the
            // first "Ask CoPilot" / 🧭 attach, which freeze the context right away
            if (this.claudyEnabled) {
                this.bindIdentity();
                this.checkPlanMode();
                this.startEventTimer();
            }
        });
        this.askSub = this.attachments.askAboutError$.subscribe((text) => this.askAboutError(text));
        this.routerSub = this.router.events.subscribe((e) => {
            if (e instanceof RouteConfigLoadEnd) {
                this.routesVersion++;
            }
        });
    }

    ngAfterViewInit(): void {
        this.stripWatcher.attach(this.tabStrip?.nativeElement);
    }

    ngDoCheck(): void {
        // W5: pure key check only (no layout reads); the measure runs when the asking tab changes
        const key = askRecheckKey(this.sessions.length, screenAskKey(this.sessions, this.activeKey));
        this.askRecheck.check(key);
    }

    ngOnDestroy(): void {
        this.stripWatcher.detach();
        clearTimeout(this.revealTimer);
        this.askRecheck.cancel();
        this.stopAllPolling();
        this.stopActivityTimer();
        this.roleSub?.unsubscribe();
        this.routerSub?.unsubscribe();
        this.askSub?.unsubscribe();
        this.logoutSub?.unsubscribe();
        clearTimeout(this.attachHintTimer);
        clearTimeout(this.historyTimer);
        clearTimeout(this.searchTimer);
        this.resizeCleanup?.();
        this.cancelVoice();
        clearTimeout(this.resolutionTimer);
        clearInterval(this.eventTimer);
        this.flushEvents();
    }

    // ----- session / panel management ---------------------------------------------------------------

    get activeSession(): ChatSession {
        return this.sessions.find((s) => s.key === this.activeKey) || this.sessions[0];
    }

    get activeMessages(): ChatMessage[] {
        return this.activeSession ? this.activeSession.messages : [];
    }

    togglePanel(): void {
        this.assistantPopover.toggle();
    }

    // ----- panel layout (W2) ------------------------------------------------------------------------

    /** Whose preferences these are (`<tenant>:<login>`, the tail of the chat storage key); '' = nobody known: nothing is stored. */
    private get scope(): string {
        return this.storageKey ? this.storageKey.substring(STORAGE_PREFIX.length + 1) : '';
    }

    private storage(): Storage | null {
        try {
            return typeof localStorage !== 'undefined' ? localStorage : null;
        } catch (e) {
            return null;
        }
    }

    /** The history sidebar covers the chat (a drawer) when the panel is narrow. */
    get narrowPanel(): boolean {
        return panelWidthPx(this.layout, viewportWidth()) < NARROW_PANEL_PX;
    }

    /** A chat in the background asks to see the screen (the sidebar toggle shows a badge while the sidebar is closed). */
    get anyScreenAsk(): boolean {
        return !!screenAskKey(this.sessions, this.activeKey);
    }

    private readStoredLayout(): PanelLayout {
        let raw: string | null = null;
        try {
            raw = this.scope ? (this.storage()?.getItem(`${LAYOUT_KEY_BASE}:${this.scope}`) ?? null) : null;
        } catch (e) {
            raw = null;
        }
        return readLayout(raw, viewportWidth());
    }

    private saveLayout(): void {
        try {
            if (this.scope) {
                this.storage()?.setItem(`${LAYOUT_KEY_BASE}:${this.scope}`, JSON.stringify(this.layout));
            }
        } catch (e) {
            // storage unavailable: the layout lasts for this page load
        }
    }

    /** Reads where the popover container is, so the fixed (docked / full-screen) panel can reach the window edges. */
    private alignDock(): void {
        const box: HTMLElement | null | undefined = this.scrollArea?.nativeElement?.closest?.('.sat-popover-container');
        if (!box) {
            return;
        }
        const rect = box.getBoundingClientRect();
        if (Math.round(rect.left) !== this.dockX || Math.round(rect.top) !== this.dockY) {
            this.dockX = Math.round(rect.left);
            this.dockY = Math.round(rect.top);
        }
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.alignDock();
    }

    toggleSidebar(): void {
        this.layout.sidebar = !this.layout.sidebar;
        this.saveLayout();
    }

    /** Docked panel <-> floating popover. The popover reopens, because a docked panel has no backdrop (the page stays usable). */
    toggleDock(): void {
        this.layout.mode = this.layout.mode === 'dock' ? 'float' : 'dock';
        this.saveLayout();
        if (this.assistantPopover?.isOpen()) {
            this.relayout = true;
            this.assistantPopover.close();
            setTimeout(() => {
                this.assistantPopover.open();
                this.relayout = false;
                setTimeout(() => this.alignDock(), 0);
            }, 0);
        }
    }

    toggleFull(): void {
        this.layout.full = !this.layout.full;
        this.saveLayout();
        setTimeout(() => this.alignDock(), 0);
    }

    /** Drag the left edge of the docked panel: the width follows the pointer (30-70% of the window). */
    startResize(event: PointerEvent): void {
        event.preventDefault();
        this.resizeCleanup?.();
        const move = (e: PointerEvent): void => {
            this.layout.widthPct = widthPctFromPointer(e.clientX, viewportWidth());
        };
        const end = (): void => {
            this.resizeCleanup?.();
            this.saveLayout();
        };
        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', end);
        document.addEventListener('pointercancel', end);
        this.resizeCleanup = () => {
            document.removeEventListener('pointermove', move);
            document.removeEventListener('pointerup', end);
            document.removeEventListener('pointercancel', end);
            this.resizeCleanup = null;
        };
    }

    /** Arrow keys on the resize handle (a keyboard way to change the width). */
    resizeBy(deltaPct: number, event?: Event): void {
        event?.preventDefault();
        this.layout.widthPct = clampWidthPct(this.layout.widthPct + deltaPct);
        this.saveLayout();
    }

    /** Esc inside the panel cancels a rename / delete, closes a sidebar drawer, else closes the panel. */
    onPanelEscape(event?: Event): void {
        event?.stopPropagation();
        if (this.renamingKey || this.confirmDeleteKey) {
            this.renamingKey = '';
            this.confirmDeleteKey = '';
        } else if (this.layout.sidebar && this.narrowPanel) {
            this.layout.sidebar = false;
        } else {
            this.assistantPopover?.close();
        }
    }

    /** The question box grows with its text, up to 40% of the panel height. */
    autoGrow(): void {
        const el: HTMLTextAreaElement | undefined = this.inputBox?.nativeElement;
        if (!el) {
            return;
        }
        el.style.height = 'auto';
        const panel = el.closest ? (el.closest('.hm-assistant') as HTMLElement | null) : null;
        const grown = growHeight(el.scrollHeight, panel?.clientHeight || 0);
        el.style.height = grown.height + 'px';
        el.style.overflowY = grown.scroll ? 'auto' : 'hidden';
    }

    /** The user typed or pasted in the box: dictation stops so the next spoken result never overwrites the typed words. */
    onBoxInput(): void {
        if (this.listening) {
            this.cancelVoice();
        }
        this.autoGrow();
    }

    async onOpened(): Promise<void> {
        // RMA-12: the chats shown are the signed-in user's own (storage is keyed by tenant + login)
        await this.bindIdentity();
        setTimeout(() => this.alignDock(), 0);
        setTimeout(() => this.alignDock(), 350); // after the popover's open animation
        if (this.relayout) {
            return; // docking / floating reopens the panel at once: not a new open
        }
        this.panelOpenedAt = Date.now();
        this.noticeVisible = !noticeDismissed(this.noticeStoreKey);
        this.track('panel_opened', {placement: 'header'});
        this.refreshFiles();
        this.checkPlanMode();
        this.syncHistory();
        this.refreshContext();
        this.scrollToBottom();
        // RMA-10: a turn that was running when the panel was closed is polled again (its reply may be ready)
        this.resumePolling();
    }

    // ----- who the chats belong to (RMA-12) and other browser tabs (RMA-28) --------------------------

    /** Binds the chat to the signed-in tenant + login: another user's stored chats are never shown or uploaded. */
    private async bindIdentity(): Promise<void> {
        let identity: any;
        try {
            identity = await this.principal.identity();
        } catch (e) {
            identity = null;
        }
        this.noticeStoreKey = noticeKey(identity);
        const key = assistantStorageKey(identity);
        if (key === this.storageKey) {
            return;
        }
        this.unbind();
        this.storageKey = key;
        this.restore();
        this.layout = this.readStoredLayout();
        this.tier = readTier(this.storage(), this.scope);
    }

    /** Drops everything of the previous user from memory (logout / another login): polling, queues, pills, history timers. */
    private unbind(): void {
        this.epoch++;
        this.stopAllPolling();
        this.stopActivityTimer();
        this.sessions.forEach((s) => this.attachments.clear(s.key));
        clearTimeout(this.historyTimer);
        this.historyDirty.clear();
        this.historyPrints.clear();
        this.historyChecked = false;
        this.historyAvailable = true;
        this.planModeChecked = false;
        this.editEnabled = true;
        this.historyRequestEnabled = false;
        this.tier = 'auto';
        this.historyQuery = '';
        this.showArchived = false;
        this.serverRows = [];
        this.searchSeq++;
        clearTimeout(this.searchTimer);
        this.renamingKey = '';
        this.confirmDeleteKey = '';
        this.cancelVoice();
        this.closedKeys = [];
        this.files = [];
        this.draft = '';
        this.uiEvents.take(); // another user's events are never sent as this one's
        this.resolutionAsk = null;
        clearTimeout(this.resolutionTimer);
        this.noticeVisible = false;
        this.noticeStoreKey = null;
        this.storageKey = null;
        this.sessions = [{key: 's' + Date.now(), label: 'Session 1', messages: []}];
        this.activeKey = this.sessions[0].key;
    }

    /** RMA-28: another browser tab saved the chats: merge them in (newer session wins), never write them back. */
    @HostListener('window:storage', ['$event'])
    onStorageEvent(event: StorageEvent): void {
        if (!this.storageKey || event.key !== this.storageKey || !event.newValue) {
            return;
        }
        let incoming: any;
        try {
            incoming = JSON.parse(event.newValue);
        } catch (e) {
            return;
        }
        const merged = mergeStoredSessions(this.sessions as any[], incoming, this.closedKeys);
        if (!merged.added.length && !merged.replaced.length && !merged.dropped.length && merged.closedKeys.length === this.closedKeys.length) {
            return;
        }
        this.closedKeys = merged.closedKeys;
        merged.dropped.forEach((key) => {
            this.attachments.clear(key);
            this.historyDirty.delete(key);
            this.historyPrints.delete(key);
        });
        const touched = new Set([...merged.added, ...merged.replaced]);
        this.sessions = (merged.sessions as ChatSession[]).map((s) => (touched.has(s.key) ? this.normaliseStored(s) : s));
        touched.forEach((key) => {
            const s = this.sessions.find((x) => x.key === key);
            if (s) {
                this.historyPrints.set(key, this.historyPrint(s));
            }
        });
        if (!this.sessions.length) {
            this.sessions = [{key: 's' + Date.now(), label: 'Session 1', messages: []}];
        }
        if (!this.sessions.some((s) => s.key === this.activeKey)) {
            this.activeKey = this.sessions[0].key;
            this.refreshFiles();
        }
    }

    /** A session read from storage: no half-finished turn, transient fields reset (as `restore` does). */
    private normaliseStored(s: ChatSession): ChatSession {
        s.messages = this.restoredMessages(s.messages);
        s.sending = false;
        s.queue = [];
        s.pollHandle = null;
        s.taskId = undefined;
        s.screenConsent = false;
        s.routesHint = undefined;
        return s;
    }

    // ----- screen context (attached on demand, suggestions, links) ---------------------------------

    /** Re-reads the screen for the screen-specific prompts (local only; nothing is sent). Never throws. */
    refreshContext(): void {
        try {
            this.contextPrompts = contextSuggestions(this.assistantContext.snapshot());
        } catch (e) {
            this.contextPrompts = [];
        }
        this.trackSuggestionsShown();
    }

    /** Pills waiting for the active chat's next message. */
    get pendingAttachments(): PendingAttachment[] {
        return this.activeSession ? this.attachments.get(this.activeSession.key) : [];
    }

    get canAddCtx(): boolean {
        return canAddContext(this.pendingAttachments);
    }

    get canAddShot(): boolean {
        return canAddScreenshot(this.pendingAttachments);
    }

    /** Tooltip of a pending pill: what goes with the next message ("Haulier Orders · list — 2 console errors, 1 failed request"). */
    pillTitle(a: PendingAttachment): string {
        if (a.kind === 'screenshot') {
            return 'Screenshot sent with your next message';
        }
        return (
            a.label +
            (a.detail ? ' — ' + a.detail : '') +
            '\nSent with your next message as it was when you attached it: this screen, its filters, field names and errors, recent clicks, ' +
            'console errors and failed requests, permissions and settings it used — never what you typed'
        );
    }

    /**
     * "Ask CoPilot" on an error toast / dialog: open the chat, attach this screen (with the error) and prefill the
     * question; the user presses send.
     */
    askAboutError(errorText: string): void {
        if (!this.claudyEnabled || !this.activeSession) {
            return;
        }
        if (!this.assistantPopover?.isOpen()) {
            this.assistantPopover?.open();
        }
        this.attachContext(errorText, 'ask');
        const text = (errorText || '').replace(/\s+/g, ' ').trim();
        this.draft = text ? `Why did I get this error: "${text.length > 300 ? text.substring(0, 299) + '…' : text}"?` : 'Why did I get this error?';
        this.draftSource = 'error_toast';
        this.draftSourceText = this.draft.trim();
        setTimeout(() => this.inputBox?.nativeElement?.focus(), 0);
    }

    /** Chat files without screenshots (those are message attachments, shown as pills). */
    get visibleFiles(): any[] {
        // the stored chat history (transcript.json) is not a file the user attached either
        // F3: what the assistant produced (outputs/) is listed as download chips under its replies, not here
        return splitFiles((this.files || []).filter((f) => !isScreenFile(f) && !isTranscriptFile(f))).inputs;
    }

    /** F3: produced files of the active chat that no reply shows yet. */
    get unclaimedOutputFiles(): OutputFileChip[] {
        return unclaimedOutputs(this.files, this.activeMessages);
    }

    /** F3: download a produced file (authenticated blob request, saved under its own name); failures say why in the chat. */
    async downloadOutput(chip: OutputFileChip): Promise<void> {
        const session = this.activeSession;
        if (!session || !chip?.path || this.downloading.has(chip.path)) {
            return;
        }
        this.downloading.add(chip.path);
        try {
            const blob = await this.assistantService.downloadFile(session.key, chip.path);
            saveBlob(blob, chip.name || fileNameOf(chip.path));
        } catch (e) {
            console.error('Assistant file download failed', e);
            session.messages.push({role: 'assistant', note: true, text: `⬇ ${chip.name}: ${downloadErrorText(e)}`});
            this.persist();
            if (session.key === this.activeKey) {
                this.scrollToBottom();
            }
        } finally {
            this.downloading.delete(chip.path);
        }
    }

    /** F3: remember the outputs before a turn is sent (once per reply); never rejects. */
    private captureOutputBaseline(session: ChatSession, placeholder: ChatMessage): Promise<unknown> {
        const known = this.outputBaselines.get(placeholder);
        if (known) {
            return known;
        }
        const baseline = this.assistantService.listFiles(session.key).then(
            (files) => outputBaseline(files),
            () => null
        );
        this.outputBaselines.set(placeholder, baseline);
        return baseline;
    }

    /** F3: after a completed turn, the outputs it produced become chips on its reply (a failed listing changes nothing). */
    private async attachProducedFiles(session: ChatSession, placeholder: ChatMessage): Promise<void> {
        try {
            const files = await this.assistantService.listFiles(session.key);
            if (session.key === this.activeKey && Array.isArray(files)) {
                this.files = files;
            }
            const baseline = this.outputBaselines.get(placeholder);
            const chips = producedSince(baseline ? await baseline : null, files);
            if (chips.length && this.sessions.includes(session)) {
                placeholder.outputFiles = chips;
                this.persist();
                if (session.key === this.activeKey) {
                    this.scrollToBottom();
                }
            }
        } catch (e) {
            // the chips are a convenience: the reply itself is already shown, and unclaimed files still list under the chat
        }
    }

    /**
     * 🧭 button: freezes what the user is looking at now as a pill for the next message. The same screen twice is not
     * added again; after moving to another screen a click adds that one too (multi-screen context).
     */
    attachContext(errorText?: string, mode: AddMode = 'add'): void {
        const session = this.activeSession;
        if (!session) {
            return;
        }
        let ctx: AssistantScreenContext;
        try {
            // the screen plus its diagnostics (console errors, failed requests, access, validation, environment…)
            ctx = this.assistantContext.attachSnapshot(errorText);
        } catch (e) {
            this.showAttachHint("Couldn't read this screen. Try again.");
            return;
        }
        // "Ask CoPilot": a same-screen pill attached before the error is refreshed so the error goes with the message
        const added = addContext(this.attachments.get(session.key), ctx, mode);
        if (added.result === 'duplicate') {
            this.showAttachHint('This screen is already attached');
        } else if (added.result === 'full') {
            this.showAttachHint(this.contextsFullText);
        } else {
            this.attachments.set(session.key, added.list);
            this.contextPrompts = contextSuggestions(ctx);
        }
    }

    /** × on a pending pill; a screenshot's uploaded file is deleted quietly too. */
    removePending(item: PendingAttachment): void {
        const session = this.activeSession;
        if (!session) {
            return;
        }
        this.attachments.set(session.key, removeAttachment(this.attachments.get(session.key), item));
        if (item.kind === 'screenshot') {
            this.assistantService
                .deleteFile(session.key, item.path || item.name)
                .then((files) => {
                    if (session.key === this.activeKey && Array.isArray(files)) {
                        this.files = files;
                    }
                })
                .catch(() => undefined);
        }
    }

    private showAttachHint(text: string): void {
        this.attachHint = text;
        clearTimeout(this.attachHintTimer);
        this.attachHintTimer = setTimeout(() => (this.attachHint = ''), 2500);
    }

    /** Screen prompts first; the general ones only on an empty chat; nothing while a turn runs. */
    get shownSuggestions(): string[] {
        if (this.activeSession?.sending) {
            return [];
        }
        const list = this.activeMessages.length === 0 ? this.contextPrompts.concat(this.suggestions) : this.contextPrompts;
        return list.filter((p, i) => list.indexOf(p) === i).slice(0, 4);
    }

    /** Text + in-app link segments of a reply (cached per message text). */
    segmentsOf(message: ChatMessage): TextSegment[] {
        if (message.role !== 'assistant' || message.error) {
            return [{text: message.display || message.text}];
        }
        const cached = this.segmentCache.get(message);
        if (cached && cached.text === message.text && cached.version === this.routesVersion) {
            return cached.segments;
        }
        if (!this.routeRoots) {
            this.routeRoots = topLevelRoutes(this.router.config);
        }
        // only paths that match a real route become links; a path into a lazy module that is not loaded yet stays
        // plain text while that module loads, then RouteConfigLoadEnd re-checks it
        const segments = splitLinks(message.text || '', typeof location !== 'undefined' ? location.origin : '', this.routeRoots, (path) =>
            this.isAppRoute(path)
        );
        this.segmentCache.set(message, {text: message.text, version: this.routesVersion, segments});
        return segments;
    }

    /**
     * CU-14zefhy21rx X5: markdown blocks of an assistant reply (cached per message text), with the same in-app links as
     * `segmentsOf`; null for the user's own messages and error replies, which stay plain text.
     */
    markdownOf(message: ChatMessage): MdBlock[] | null {
        if (message.role !== 'assistant' || message.error || !message.text) {
            return null;
        }
        const cached = this.markdownCache.get(message);
        if (cached && cached.text === message.text && cached.version === this.routesVersion) {
            return cached.blocks;
        }
        if (!this.routeRoots) {
            this.routeRoots = topLevelRoutes(this.router.config);
        }
        const origin = typeof location !== 'undefined' ? location.origin : '';
        let blocks: MdBlock[];
        try {
            blocks = parseMarkdown(message.text, (text) => splitLinks(text, origin, this.routeRoots, (path) => this.isAppRoute(path)));
        } catch (e) {
            blocks = null; // never lose a reply over its formatting: show it as plain text
        }
        this.markdownCache.set(message, {text: message.text, version: this.routesVersion, blocks});
        return blocks;
    }

    /** Real record paths for the assistant (null when they cannot be read; the message still goes). */
    private safeRoutesHint(url?: string): string[] | null {
        try {
            return routesHint(this.router.config as any, url || this.router.url);
        } catch (e) {
            return null;
        }
    }

    private isAppRoute(path: string): boolean {
        try {
            const config = this.router.config as any;
            const match = matchRoute(path, config);
            if (match === 'unknown') {
                this.routePreloader.load(unloadedLazyRoutes(path, config));
            }
            return match === 'yes';
        } catch (e) {
            return true; // never hide a link because the check failed; the click still reports a dead one
        }
    }

    /**
     * Opens an in-app link from a reply inside the app (same tab, router navigation); the chat stays open. A link the
     * router cannot open (no such page, or not allowed) says so in the chat instead of silently staying put.
     */
    openLink(event: Event, path: string): void {
        event.preventDefault();
        this.track('link_clicked', {target: (path || '').split(/[/?#]/).filter(Boolean)[0] || ''});
        const session = this.activeSession; // links are only clicked in the chat on show
        const failed = () => {
            if (session) {
                session.messages.push({
                    role: 'assistant',
                    note: true,
                    text: `🔗 Couldn't open ${path} — that page doesn't exist or isn't available to you.`
                });
                this.persist();
                if (session.key === this.activeKey) {
                    this.scrollToBottom();
                }
            }
        };
        this.router.navigateByUrl(path).then(
            (ok) => (ok === false ? failed() : setTimeout(() => this.refreshContext(), 300)),
            () => failed()
        );
    }

    /** Asks the server once per page load whether changes (Plan → Approve) are enabled; any failure keeps read-only. */
    private async checkPlanMode(): Promise<void> {
        if (this.planModeChecked) {
            return;
        }
        this.planModeChecked = true;
        try {
            const state = await this.assistantService.getState(this.activeKey);
            this.editEnabled = editAllowed(state?.capabilities);
            if (state?.planMode === true) {
                this.setPlanMode(true);
            }
            this.bugReportEnabled = state?.bugReport === true;
            this.historyRequestEnabled = state?.historyRequest === true;
            // attached contexts carry it as env.dateFormat (null = the tenant has none / older server)
            this.attachments.dateFormat = tenantDateFormat(state?.dateFormat);
        } catch (e) {
            this.planModeChecked = false; // try again on the next open
        }
    }

    private setPlanMode(on: boolean): void {
        this.planMode = on;
        this.modeText = chatModeText(on, this.editEnabled);
    }

    onClosed(): void {
        this.dragging = false;
        this.resizeCleanup?.();
        if (this.relayout) {
            return; // docking / floating reopens the panel at once: not a close
        }
        this.stopAllPolling();
        this.track('panel_closed', {open_ms: this.panelOpenedAt ? Date.now() - this.panelOpenedAt : 0});
        this.panelOpenedAt = 0;
        this.suggestionsShownKey = '';
        this.flushEvents();
    }

    selectSession(key: string): void {
        this.confirmDeleteKey = '';
        this.activeKey = key;
        this.files = []; // RMA-47: never show the previous tab's files while this tab's list loads
        this.refreshFiles();
        this.scrollToBottom();
        this.revealTab(key); // W5: the opened tab (e.g. from the pinned "!") scrolls into view
        this.trackSuggestionsShown();
    }

    newSession(): void {
        this.closeDrawer();
        const current = this.activeSession;
        if (current && !this.hasTurns(current) && !current.sending && !current.queue?.length) {
            setTimeout(() => this.inputBox?.nativeElement?.focus(), 0); // the chat on screen is already empty: no second one
            return;
        }
        const key = 's' + Date.now();
        // X1 (CU-14zefhy29pp): the count as before, moved on while an open tab already has that label
        const label = freeSessionLabel(
            this.sessions.map((s) => s.label),
            this.sessions.length + 1
        );
        this.sessions.push({key, label, messages: []});
        this.activeKey = key;
        this.files = [];
        this.persist();
        this.revealTab(key); // W5: a new tab added past the strip edge shows as active
        this.trackSuggestionsShown();
    }

    /** RMA-48: a screen request is waiting for an answer in a tab that is not the one on show. */
    hasWaitingScreenRequest(session: ChatSession): boolean {
        return needsScreenAnswer(session, this.activeKey);
    }

    /** W5: measure whether the asking tab is scrolled out of the strip and cache it in `hiddenAsk`. */
    private refreshHiddenAsk(): void {
        const key = screenAskKey(this.sessions, this.activeKey);
        const geometry = key ? this.tabGeometry(key) : null;
        let next: ChatSession | null = null;
        if (key && geometry) {
            const inView = revealScrollLeft(geometry.view, geometry.item) === geometry.view.scrollLeft;
            next = inView ? null : this.sessions.find((s) => s.key === key) || null;
        }
        if (next !== this.hiddenAsk) {
            this.hiddenAsk = next;
            this.cdr.markForCheck();
        }
    }

    /** W5: the "!" for a hidden asking tab shows or hides as the strip scrolls. */
    onTabsScroll(): void {
        this.refreshHiddenAsk();
    }

    /** W5: scroll the tab strip so the tab of this chat is fully visible. */
    private revealTab(key: string): void {
        clearTimeout(this.revealTimer);
        this.revealTimer = setTimeout(() => {
            const geometry = this.tabGeometry(key);
            if (geometry) {
                const left = revealScrollLeft(geometry.view, geometry.item);
                if (left !== geometry.view.scrollLeft) {
                    geometry.strip.scrollTop = left; // the history list is vertical: "left" / "width" of the helper mean top / height
                }
            }
            this.refreshHiddenAsk();
        }, 0);
    }

    private tabGeometry(key: string): {strip: HTMLElement; view: {scrollLeft: number; width: number}; item: {left: number; width: number}} | null {
        const strip: HTMLElement | undefined = this.tabStrip?.nativeElement;
        const tab: HTMLElement | undefined = strip
            ? Array.from(strip.querySelectorAll<HTMLElement>('[data-session-key]')).find((el) => el.dataset.sessionKey === key)
            : undefined;
        if (!strip || !tab || !strip.clientHeight) {
            return null;
        }
        const stripRect = strip.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();
        return {
            strip,
            view: {scrollLeft: strip.scrollTop, width: strip.clientHeight},
            item: {left: tabRect.top - stripRect.top + strip.scrollTop, width: tabRect.height}
        };
    }

    // ----- chat history sidebar (W2) -----------------------------------------------------------------

    /** A local note or a screen-request card is not a turn of the conversation. */
    private isTurn(m: ChatMessage): boolean {
        return !m.note && !m.screen;
    }

    private hasTurns(session: ChatSession): boolean {
        return session.messages.some((m) => this.isTurn(m));
    }

    /** What the sidebar shows for a chat: its title, else the first question, else "New chat". */
    displayLabel(session: ChatSession): string {
        if (!isAutoLabel(session.label)) {
            return session.label;
        }
        const first = session.messages.find((m) => m.role === 'user');
        return titleFromQuestion(first?.display || first?.text) || 'New chat';
    }

    /** The sidebar rows of this browser's chats, pinned first, then newest first, grouped by date. */
    get historyList(): SidebarRow[] {
        const q = this.historyQuery.trim();
        const items: HistoryItem[] = [];
        for (const s of this.sessions) {
            if (!this.hasTurns(s) && s.key !== this.activeKey) {
                continue; // an empty chat is not history (only the one on screen shows, as "New chat")
            }
            const lastAt = s.updatedAt || [...s.messages].reverse().find((m) => m.createdAt)?.createdAt || Date.now();
            items.push({
                key: s.key,
                label: this.displayLabel(s),
                updatedAt: lastAt,
                pinned: !!s.pinned,
                archived: !!s.archived,
                text: q ? s.messages.map((m) => (m.text || '').toLowerCase()).join(' ') : undefined
            });
        }
        for (const r of this.serverRows) {
            if (!this.sessions.some((s) => s.key === r.key) && !this.closedKeys.includes(r.key)) {
                items.push({
                    key: r.key,
                    label: r.label || 'Earlier chat',
                    updatedAt: r.updatedAt,
                    pinned: r.pinned === true,
                    archived: r.archived === true
                });
            }
        }
        const byKey = new Map(this.sessions.map((s) => [s.key, s]));
        return historyRows(items, this.historyQuery, this.showArchived, Date.now(), this.activeKey).map((r) => ({...r, session: byKey.get(r.key)}));
    }

    rowKey = (_index: number, row: {key: string}): string => row.key;

    /** RMA-29: the accessible name of a chat (says when its assistant waits for an answer about the screen). */
    tabLabel(row: {label: string; session?: ChatSession}): string {
        return row.session && this.hasWaitingScreenRequest(row.session) ? `${row.label} - ${this.label} asks to see your screen` : row.label;
    }

    openChat(row: SidebarRow): void {
        this.cancelRename();
        if (row.session) {
            this.selectSession(row.key);
        } else {
            void this.openArchived(row.key);
        }
        this.closeDrawer();
    }

    /** An archived / found chat only the server holds: read its transcript, open it as a tab. */
    private async openArchived(key: string): Promise<void> {
        const epoch = this.epoch;
        const info = this.serverRows.find((r) => r.key === key) || null;
        await this.loadTranscript(key, info);
        if (epoch !== this.epoch) {
            return;
        }
        const opened = this.sessions.find((s) => s.key === key);
        if (!opened) {
            this.noteActive('Could not open that chat. Try again.');
            return;
        }
        opened.archived = info?.archived === true;
        this.serverRows = this.serverRows.filter((r) => r.key !== key);
        this.persist();
        this.selectSession(key);
        this.cdr.markForCheck();
    }

    /** The search box: the title search also asks the server (debounced), so archived chats are always found. */
    onHistoryQuery(value: string): void {
        this.historyQuery = value || '';
        clearTimeout(this.searchTimer);
        const q = this.historyQuery.trim();
        if (q.length < 2) {
            this.searchSeq++;
            if (!this.showArchived) {
                this.serverRows = [];
            } else {
                void this.loadServerRows('');
            }
            return;
        }
        this.searchTimer = setTimeout(() => void this.loadServerRows(q), 300);
    }

    onShowArchived(on: boolean): void {
        this.showArchived = !!on;
        if (this.showArchived) {
            const q = this.historyQuery.trim();
            void this.loadServerRows(q.length >= 2 ? q : '');
        } else if (this.historyQuery.trim().length < 2) {
            this.searchSeq++;
            this.serverRows = [];
        }
    }

    private async loadServerRows(q: string): Promise<void> {
        const seq = ++this.searchSeq;
        const epoch = this.epoch;
        try {
            const rows = parseServerSessions(await this.assistantService.listSessions(q, true));
            if (epoch !== this.epoch || seq !== this.searchSeq) {
                return;
            }
            this.serverRows = rows.filter((r) => (q ? true : r.archived === true));
        } catch (e) {
            if (seq === this.searchSeq) {
                this.serverRows = [];
            }
        }
        this.cdr.markForCheck();
    }

    /** The server list holds the 50 newest unpinned chats and the pinned ones: a saved, unlisted, unchanged tab is archived. */
    private applyArchivedFlags(server: ServerSessionInfo[]): void {
        const listed = new Set(server.map((s) => s.key));
        for (const s of this.sessions) {
            if (listed.has(s.key)) {
                s.archived = false;
            } else if (s.revision !== undefined && !s.unsynced && !s.sending && s.messages.length) {
                s.archived = true;
            }
        }
    }

    togglePin(row: SidebarRow): void {
        row.session.pinned = !row.session.pinned;
        this.persist(); // the pin travels with the transcript
    }

    startRename(row: SidebarRow): void {
        this.confirmDeleteKey = '';
        this.renamingKey = row.key;
        this.renameText = row.label;
        setTimeout(() => {
            const input: HTMLInputElement | null | undefined = this.tabStrip?.nativeElement?.querySelector('input.hm-assistant__rename');
            input?.focus();
            input?.select();
        }, 0);
    }

    commitRename(event?: Event): void {
        event?.preventDefault();
        const key = this.renamingKey;
        if (!key) {
            return;
        }
        this.renamingKey = '';
        const session = this.sessions.find((s) => s.key === key);
        const label = cleanLabel(this.renameText);
        if (session && label && label !== session.label) {
            session.label = label;
            this.persist();
        }
    }

    cancelRename(event?: Event): void {
        event?.stopPropagation();
        this.renamingKey = '';
    }

    /** The trash icon: a chat with messages asks "Delete / Keep" first; an empty one goes at once. */
    askDelete(row: SidebarRow, event: Event): void {
        event.stopPropagation();
        if (!this.hasTurns(row.session)) {
            this.deleteChat(row, event);
            return;
        }
        this.confirmDeleteKey = row.key;
    }

    cancelDelete(event: Event): void {
        event.stopPropagation();
        this.confirmDeleteKey = '';
    }

    deleteChat(row: SidebarRow, event: Event): void {
        event.stopPropagation();
        this.confirmDeleteKey = '';
        this.closeSession(row.session, event);
    }

    /** The chat to show after one went away: the most recently changed one with messages. */
    private mostRecentKey(): string {
        const list = [...this.sessions].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
        return (list.find((s) => this.hasTurns(s)) || list[0]).key;
    }

    /** A narrow panel shows the history as a drawer over the chat: it closes once a chat is chosen. */
    private closeDrawer(): void {
        if (this.layout.sidebar && this.narrowPanel) {
            this.layout.sidebar = false;
        }
    }

    // ----- voice input (W3) ----------------------------------------------------------------------------

    /** Mic button: starts or stops dictation into the question box (the browser's own speech recognition; no audio is kept here). */
    toggleVoice(): void {
        if (this.listening) {
            const current = this.dictation;
            const stopToken = ++this.voiceToken;
            if (current && !current.listening) {
                this.listening = false; // stopped before the microphone prompt was answered
            }
            current?.stop();
            // a browser that never reports the end (no speech service): the next click must still start a new dictation
            setTimeout(() => {
                if (stopToken === this.voiceToken && this.dictation === current && this.listening) {
                    this.dictation = null;
                    this.listening = false;
                }
            }, 3000);
            return;
        }
        this.voiceError = '';
        this.dictationIgnore = false;
        if (!this.dictation) {
            const mine: AssistantDictation = new AssistantDictation(window, {
                onText: (text) => {
                    if (this.dictationIgnore || this.dictation !== mine || !this.listening) {
                        return;
                    }
                    this.draft = text;
                    setTimeout(() => this.autoGrow(), 0);
                },
                onEnd: (error) => {
                    if (this.dictation !== mine) {
                        return; // an older dictation that was given up
                    }
                    this.listening = false;
                    this.dictationIgnore = false;
                    this.voiceError = error || '';
                    setTimeout(() => this.inputBox?.nativeElement?.focus(), 0);
                }
            });
            this.dictation = mine;
        }
        // ask for the microphone first so the browser shows its prompt; stop() meanwhile cancels the start
        const dictation = this.dictation;
        this.voiceToken++;
        this.listening = true;
        void dictation
            .startAfterMicrophone(this.draft, () => ensureMicrophone(typeof navigator !== 'undefined' ? navigator : null))
            .then((outcome) => {
                if (!outcome || this.dictation !== dictation) {
                    return; // cancelled
                }
                if (!outcome.started) {
                    this.listening = false;
                    this.voiceError = outcome.error || '';
                }
                this.track('voice_clicked', {started: outcome.started});
            });
    }

    /** Ends or cancels dictation (Send, typing, a new chat): a start waiting for the microphone prompt never goes live. */
    private cancelVoice(): void {
        this.dictationIgnore = true;
        const dictation = this.dictation;
        if (dictation && !dictation.listening) {
            this.listening = false;
        }
        dictation?.stop();
    }

    // ----- model tier (W3, C5) -------------------------------------------------------------------------

    /** The chat has a question already (Fast is offered before the first one only). */
    private get chatStarted(): boolean {
        return !!this.activeSession && this.activeSession.messages.some((m) => m.role === 'user');
    }

    get tierOptions(): TierOption[] {
        return tierOptionsFor(this.chatStarted);
    }

    /** The option shown as chosen: Fast no longer applies once the chat has a question. */
    get shownTier(): TierPreference {
        return effectiveTier(this.tier, this.chatStarted);
    }

    pickTier(option: TierOption): void {
        if (!option.enabled) {
            return;
        }
        this.tier = normalizeTier(option.value);
        saveTier(this.storage(), this.scope, this.tier);
        this.track('tier_selected', {tier: this.tier});
    }

    /** "Answered by Sonnet · Fast" under a finished reply; '' when the server named nothing. */
    answeredByText(m: ChatMessage): string {
        return answeredByLabel(m.answeredBy);
    }

    /** RMA-10: polls again every session whose turn was still running when the panel was closed. */
    private resumePolling(): void {
        this.sessions.forEach((s) => {
            if (s.sending && s.taskId && !s.pollHandle) {
                const placeholder = [...s.messages].reverse().find((m) => m.pending);
                if (placeholder) {
                    this.pollTask(s, s.taskId, placeholder);
                }
            }
        });
    }

    closeSession(session: ChatSession, event: Event): void {
        event.stopPropagation();
        this.track('session_closed', {turn_count: completedTurns(session.messages), resolution_shown: !!session.resolutionAsked}, session);
        this.offerResolution(session, 'close');
        // RMA-13: the closed tab's turn stops being followed, its queue is dropped and nothing more is shared from it
        this.stopPolling(session);
        session.queue = [];
        session.sending = false;
        session.taskId = undefined;
        session.screenConsent = false;
        session.messages.forEach((m) => {
            if (m.screen?.state === 'asking') {
                m.screen.state = 'expired';
                this.assistantService.declineScreen(session.key, m.screen.id).catch(() => undefined);
            }
        });
        this.sessions = this.sessions.filter((s) => s.key !== session.key);
        this.attachments.clear(session.key);
        // the server copy goes too, so the tab does not come back here or on another device
        this.closedKeys = addClosedKey(this.closedKeys, session.key);
        this.historyDirty.delete(session.key);
        this.historyPrints.delete(session.key);
        if (this.historyAvailable) {
            this.assistantService.deleteTranscript(session.key).catch(() => undefined);
        }
        if (!this.sessions.length) {
            // the last chat was deleted: a fresh one
            const key = 's' + Date.now();
            this.sessions = [{key, label: 'Session 1', messages: []}];
            this.historyPrints.set(key, this.historyPrint(this.sessions[0]));
        }
        if (this.activeKey === session.key) {
            this.activeKey = this.mostRecentKey();
            this.files = [];
            this.refreshFiles();
            this.scrollToBottom();
        }
        this.persist();
    }

    useSuggestion(prompt: string): void {
        this.draft = prompt;
        setTimeout(() => this.autoGrow(), 0);
        this.draftSource = 'suggestion';
        this.draftSourceText = prompt.trim();
        this.track('suggestion_used', {kind: this.contextPrompts.includes(prompt) ? 'screen' : 'general'});
        setTimeout(() => this.inputBox?.nativeElement?.focus(), 0);
    }

    // ----- sending / polling ------------------------------------------------------------------------

    onKeydown(event: KeyboardEvent): void {
        // RMA-49: the Enter that confirms an IME composition must not send the message
        if (isImeComposing(event)) {
            return;
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.send();
        }
    }

    async send(): Promise<void> {
        const text = this.draft.trim();
        if (!text) {
            return;
        }
        const session = this.activeSession;
        const entryPoint = entryPointOf(this.draftSource, this.draftSourceText, text);
        if (this.listening) {
            this.cancelVoice(); // words the browser still delivers after Send must not refill the box
        }
        this.draft = '';
        setTimeout(() => this.autoGrow(), 0); // the question box shrinks back
        this.draftSource = undefined;
        this.draftSourceText = '';
        session.lastActivityAt = Date.now();
        // Same-session sends queue up behind the in-flight turn; a different session is free to run in parallel.
        // The pills pending now go with this message (screen contexts were frozen when the user attached them).
        (session.queue = session.queue || []).push({text, pending: this.attachments.take(session.key), entryPoint, tier: this.tier});
        this.persist();
        if (!session.sending) {
            this.processQueue(session);
        }
    }

    /** Starts the next queued message for a session; chained from finishTurn so a session drains its own queue in order. */
    private async processQueue(session: ChatSession): Promise<void> {
        if (session.sending) {
            return;
        }
        const queued = session.queue && session.queue.shift();
        if (!queued || !queued.text) {
            return;
        }
        const text = queued.text;
        const pending = queued.pending || [];
        // Retry resends the last user message: no second bubble
        const lastMessage = session.messages[session.messages.length - 1];
        const reuse = queued.retry && lastMessage && lastMessage.role === 'user' ? lastMessage : null;
        // local notes / screen cards are not turns: the conversation is fresh until the first user message
        const fresh = reuse ? session.messages.filter((m) => m.role === 'user').length <= 1 : !session.messages.some((m) => m.role === 'user');
        const userMessage: ChatMessage = reuse || {role: 'user', text, id: newMessageId(), createdAt: Date.now()};
        if (!reuse) {
            if (queued.display) {
                userMessage.display = queued.display;
            }
            if (pending.length) {
                userMessage.attachments = sentAttachments(pending);
            }
            session.messages.push(userMessage);
            if (fresh && isAutoLabel(session.label)) {
                session.label = titleFromQuestion(queued.display || text) || session.label; // the chat is titled by its first question
            }
        }
        // the server names the screenshots in the prompt itself; the text goes as typed
        let attach = turnPayload(pending);
        // the real in-app paths go (top level, context or not) with the first message of a chat and again when they change,
        // so the links in its replies work; ordered for the first attached screen, else the page the user is on
        const hint = this.safeRoutesHint(attach.contexts?.[0]?.url);
        const hintKey = hint ? routesHintKey(hint) : '';
        const sendRoutes = !!hint && (fresh || session.routesHint !== hintKey);
        if (sendRoutes) {
            attach = withRoutesHint(attach, hint);
        }
        const placeholder: ChatMessage = {
            role: 'assistant',
            text: '',
            pending: true,
            id: newMessageId(),
            createdAt: Date.now(),
            retryOf: queued.bug ? undefined : text // a bug draft is not retried as plain text
        };
        session.messages.push(placeholder);
        session.lastActivityAt = Date.now();
        if (pending.length) {
            this.turnPending.set(placeholder, pending);
        }
        if (queued.bug) {
            // the report goes with the same frozen screens / screenshots the assistant drafted it from
            this.bugTurns.set(placeholder, {contexts: attach.contexts, screenshots: attach.screenshots});
        }
        session.sending = true;
        this.persist();
        if (session.key === this.activeKey) {
            this.scrollToBottom();
        }

        try {
            // F3: the outputs present before this turn are listed BEFORE the message is sent, so a file the model creates
            // in its first seconds is not mistaken for an older one (a failed listing = unknown baseline)
            await this.captureOutputBaseline(session, placeholder);
            const escalate = wantsEscalate(queued, session);
            const started = await this.assistantService.sendMessage(
                session.key,
                text,
                fresh,
                attach,
                turnExtras(escalate, queued.entryPoint, userMessage.id, tierField(effectiveTier(queued.tier ?? this.tier, !fresh)))
            );
            if (!this.sessions.includes(session)) {
                return; // RMA-13: the tab was closed while the message was being sent: nothing follows this turn
            }
            if (started.error || !started.taskId) {
                this.restorePending(session, pending, placeholder);
                this.failPlaceholder(session, placeholder, started.error || `${ASSISTANT_LABEL} could not start. Try again.`);
                return;
            }
            escalateUsed(session); // the better model was asked for once
            this.markTurn([userMessage, placeholder], started);
            if (sendRoutes) {
                session.routesHint = hintKey;
                this.persist();
            }
            this.pollTask(session, started.taskId, placeholder);
        } catch (e) {
            this.restorePending(session, pending, placeholder);
            // D-502: no app-wide error dialog for CoPilot calls any more; the chat shows the server's reason itself
            this.failPlaceholder(session, placeholder, assistantErrorReason(e) || `Could not reach ${ASSISTANT_LABEL}. Try again.`);
        }
    }

    /**
     * A turn that never started gives its pills back to the composer so the user can resend them, unless the user has
     * attached something new meanwhile (then those are kept and the old ones dropped).
     */
    private restorePending(session: ChatSession, pending: PendingAttachment[], placeholder: ChatMessage): void {
        this.turnPending.delete(placeholder); // back in the composer: Retry must not attach them twice
        if (pending && pending.length && !this.attachments.get(session.key).length && this.sessions.includes(session)) {
            this.attachments.set(session.key, pending);
        }
    }

    private pollTask(session: ChatSession, taskId: string, placeholder: ChatMessage): void {
        this.stopPolling(session);
        if (!taskId) {
            // never poll chat/events/ with an empty task id (the API answers 500)
            this.failPlaceholder(session, placeholder, `${ASSISTANT_LABEL} could not start. Try again.`);
            return;
        }
        session.taskId = taskId; // RMA-10: reopening the panel after a close resumes this poll
        // X2 / T3: failed polls retry with a growing pause (1.5 s up to 10 s); a server that stays unreachable for
        // POLL_LOST_AFTER_MS (5 min) ends the turn, and so does a refusal (401 / 403) or a gone task (404 / 410);
        // the API's retryable "still working" answers are bounded by time (POLL_MAX_QUIET_MS without a successful poll) instead
        const startedAt = Date.now();
        let lastOkAt = startedAt;
        let lastReachedAt = startedAt;
        let failures = 0;
        let hardFailures = 0;
        let narrated = false;
        const tick = async () => {
            const handle = session.pollHandle;
            let status: AssistantTaskStatus;
            try {
                status = await this.assistantService.getEvents(taskId);
            } catch (e) {
                if (session.pollHandle !== handle) {
                    return; // polling was stopped (panel destroyed / turn restarted) while this request ran
                }
                failures++;
                // the server answering "ask again" proves the connection is fine: only real failures count in a row
                const reached = isRetryablePollAnswer(e);
                hardFailures = reached ? 0 : hardFailures + 1;
                if (reached) {
                    lastReachedAt = Date.now(); // the server answered "ask again": the connection is fine
                }
                const decision = onPollFailure(e, failures, Date.now() - lastOkAt, Date.now() - lastReachedAt);
                if (decision.giveUp) {
                    this.failPlaceholder(session, placeholder, LOST_CONNECTION_TEXT);
                    return;
                }
                if (!narrated) {
                    placeholder.text = waitingText(Date.now() - startedAt, hardFailures);
                }
                session.pollHandle = setTimeout(tick, decision.delayMs);
                return;
            }
            if (session.pollHandle !== handle) {
                return;
            }
            failures = 0;
            hardFailures = 0;
            lastOkAt = Date.now();
            lastReachedAt = lastOkAt;
            const state = (status?.status || '').toLowerCase();
            const running = !isTerminalTaskState(state);
            this.handleScreenRequest(session, placeholder, running ? status?.screenRequest : null);
            if (state === 'completed') {
                placeholder.pending = false;
                placeholder.stopped = false;
                placeholder.retryOf = undefined;
                placeholder.completedAt = Date.now();
                this.setActivity(placeholder, null);
                const raw = status?.result?.result || '';
                const plan = toPlanCard(status?.pendingPlan);
                const bug = this.bugCardFor(session, placeholder, raw, status?.bugDraft);
                const changes = toChangesCard(status?.changes, taskId);
                placeholder.text =
                    this.replyText(raw) ||
                    (plan ? 'Here is the change I propose.' : bug ? 'Here is a draft bug report. Check it and press Submit.' : 'Done.');
                if (plan) {
                    placeholder.plan = plan;
                    this.setPlanMode(true); // a plan only arrives when the server has Plan → Approve on
                }
                if (bug) {
                    placeholder.bug = bug;
                }
                if (changes) {
                    placeholder.changes = applyUndoStatus(changes, status?.undoStatus);
                }
                const answeredBy = parseAnsweredBy(status?.answeredBy);
                if (answeredBy) {
                    placeholder.answeredBy = answeredBy;
                }
                // W4 (C3): the answer was limited to a period: say which, offer the full history
                const dw = status?.dataWindow;
                if (dw?.from && dw?.to) {
                    placeholder.dataWindow = {days: dw.days, from: dw.from, to: dw.to, requestable: dw.requestable === true};
                }
                const undoPlanId = this.undoTurns.get(placeholder);
                if (undoPlanId) {
                    // the approved revert turn really finished: only now is the original change undone
                    this.undoTurns.delete(placeholder);
                    this.setUndoState(session, undoPlanId, 'undone');
                }
                // F3: the chips are attached BEFORE the turn ends, so the next queued turn cannot leak files into this reply
                await this.attachProducedFiles(session, placeholder);
                if (session.pollHandle !== handle) {
                    return; // polling was stopped (tab closed / turn restarted) while the listing ran
                }
                this.finishTurn(session);
            } else if (state === 'cancelled' && cancelledOutcome(placeholder)) {
                // the user pressed Stop: not an error
                placeholder.pending = false;
                this.setActivity(placeholder, null);
                placeholder.text = cancelledOutcome(placeholder)!.text;
                placeholder.completedAt = Date.now();
                this.finishTurn(session);
            } else if (state === 'failed' || state === 'cancelled') {
                this.failPlaceholder(session, placeholder, status?.error || `${ASSISTANT_LABEL} could not finish this request.`);
            } else {
                // still pending/running — keep the latest narrative as a live hint, then poll again
                // RMA-61: what the turn is doing now (a poll without it keeps the last one known)
                const activity = toActivity(status?.activity, startedAt);
                if (activity) {
                    this.setActivity(placeholder, activity);
                }
                const narrative = this.replyText(this.latestNarrative(status));
                if (narrative) {
                    placeholder.text = narrative;
                    narrated = true;
                } else if (!narrated) {
                    // the activity line already says the turn is working: no extra "Still working…" under it
                    placeholder.text = placeholder.activity ? '' : waitingText(Date.now() - startedAt, 0);
                }
                session.pollHandle = setTimeout(tick, POLL_INTERVAL_MS);
            }
        };
        session.pollHandle = setTimeout(tick, POLL_INTERVAL_MS);
    }

    // ----- screen requests (the assistant asks to see the screen) -----------------------------------

    /**
     * Shows a new screen request once (consent card before the reply placeholder), answers it straight away when the
     * user already allowed it for this chat, and closes cards the server stopped asking about (answered elsewhere,
     * timed out, or the turn ended).
     */
    private handleScreenRequest(session: ChatSession, placeholder: ChatMessage, raw: any): void {
        const request = toScreenRequest(raw);
        let changed = false;
        session.messages.forEach((m) => {
            if (m.screen && m.screen.state === 'asking' && (!request || m.screen.id !== request.id)) {
                m.screen.state = 'expired';
                changed = true;
            }
        });
        if (request && !session.messages.some((m) => m.screen?.id === request.id)) {
            const card: ChatMessage = {role: 'assistant', text: '', screen: {id: request.id, reason: request.reason, state: 'asking'}};
            const at = session.messages.indexOf(placeholder);
            session.messages.splice(at >= 0 ? at : session.messages.length, 0, card);
            changed = true;
            if (session.screenConsent) {
                this.shareScreen(session, card);
            } else if (session.key === this.activeKey) {
                this.scrollToBottom();
            }
        }
        if (changed) {
            this.persist();
        }
    }

    /** "Allow for this chat": share now and answer later requests in this chat without asking again. */
    allowScreen(message: ChatMessage): void {
        const session = this.sessionOf(message);
        if (!session || message.screen?.state !== 'asking') {
            return;
        }
        session.screenConsent = true;
        this.shareScreen(session, message);
    }

    /** "Not now": tell the assistant so it stops waiting (404 = the request already closed — nothing to do). */
    async declineScreen(message: ChatMessage): Promise<void> {
        const session = this.sessionOf(message);
        if (!session || message.screen?.state !== 'asking') {
            return;
        }
        message.screen.state = 'declined';
        this.persist();
        try {
            await this.assistantService.declineScreen(session.key, message.screen.id);
        } catch (e) {
            // the tool times out on its own; nothing else to do
        }
    }

    screenStateText(screen: ScreenAsk): string {
        const text = SCREEN_STATE_TEXT[screen.state] || '';
        return screen.state === 'shared' && screen.reason ? `${text} — ${screen.reason}` : text;
    }

    private async shareScreen(session: ChatSession, message: ChatMessage): Promise<void> {
        const ask = message.screen;
        ask.state = 'sharing';
        this.persist();
        try {
            const shot = await captureVisibleScreen();
            const files = await this.assistantService.uploadFile(session.key, shot.blob, screenFileName(ask.id, shot.mime), ask.id);
            if (session.key === this.activeKey && Array.isArray(files)) {
                this.files = files;
            }
            ask.state = 'shared';
        } catch (e) {
            console.error('Assistant screen share failed', e);
            ask.state = e instanceof ScreenshotTimeoutError ? 'timedOut' : 'failed';
            // the assistant stops waiting at once and learns why there is no picture
            this.assistantService.declineScreen(session.key, ask.id, captureFailureReason(e)).catch(() => undefined);
        }
        this.persist();
    }

    /**
     * Camera button: screenshot now, shown as a pill and sent with the next message (max 3). It is uploaded at once so
     * the send is not slowed; it never shows in the "Attach files" list.
     */
    async attachScreenshot(): Promise<void> {
        const session = this.activeSession;
        if (!session || this.capturing) {
            return;
        }
        if (!canAddScreenshot(this.attachments.get(session.key))) {
            this.showAttachHint(this.shotsFullText);
            return;
        }
        this.capturing = true;
        try {
            const shot = await captureVisibleScreen();
            const name = screenFileName(null, shot.mime);
            const files = await this.assistantService.uploadFile(session.key, shot.blob, name);
            if (session.key === this.activeKey && Array.isArray(files)) {
                this.files = files;
            }
            const entry = Array.isArray(files) ? files.find((f) => baseName(f) === name) : null;
            const path = entry && entry.path ? String(entry.path) : undefined;
            if (this.sessions.includes(session)) {
                const added = addScreenshot(this.attachments.get(session.key), name, path);
                this.attachments.set(session.key, added.list);
            }
            return; // the pill is the confirmation; no transcript note
        } catch (e) {
            console.error('Assistant screenshot failed', e);
            session.messages.push({
                role: 'assistant',
                note: true,
                text:
                    e instanceof ScreenshotTimeoutError
                        ? '📷 The screenshot took too long. Try again or attach a file.'
                        : "📷 Couldn't take the screenshot. Try again or attach a file."
            });
        } finally {
            this.capturing = false;
        }
        this.persist();
        this.scrollToBottom();
    }

    // ----- report a bug (item 7) --------------------------------------------------------------------

    /** Reply text without the fenced plan / bug / changes blocks (the server strips them too; this guards raw results). */
    private replyText(raw: string): string {
        return stripChangesBlock(stripBugBlock(stripPlanBlock(raw || '')));
    }

    /**
     * Header 🐞 or the link under an error reply: attaches this screen (with its errors) and asks the assistant to draft
     * a report from the conversation. The draft comes back as an editable form; nothing is filed until Submit.
     */
    reportBug(): void {
        const session = this.activeSession;
        if (!this.bugReportEnabled || !session || session.sending) {
            return;
        }
        // like "Ask CoPilot": a same-screen pill is refreshed, a full list keeps what the user attached
        this.attachContext(undefined, 'ask');
        (session.queue = session.queue || []).push({
            text: BUG_DRAFT_PROMPT,
            display: BUG_DRAFT_LABEL,
            pending: this.attachments.take(session.key),
            bug: true,
            entryPoint: 'bug'
        });
        this.persist();
        this.processQueue(session);
    }

    /** The editable report for a finished turn: always for a "Report a bug" turn, else only when the reply holds a draft. */
    private bugCardFor(session: ChatSession, placeholder: ChatMessage, raw: string, serverDraft: any): BugCard | null {
        const turn = this.bugTurns.get(placeholder);
        const draft = toBugDraft(serverDraft) || parseBugBlock(raw);
        if (!turn && !(draft && this.bugReportEnabled)) {
            return null;
        }
        // screenshots: only the ones that went with the drafting turn (as on RouteMagic), and only names the bug-report
        // API accepts (`screen-*`; legacy `screenshot-<n>` names would make it reject the whole report)
        const screenshots = bugScreenshots(turn?.screenshots || [], (n) => SCREEN_FILE.test(n));
        const card: BugCard = {draft: draft || emptyBugDraft(), state: 'editing'};
        if (turn?.contexts?.length) {
            card.contexts = turn.contexts;
        }
        if (screenshots.length) {
            card.screenshots = screenshots;
        }
        return card;
    }

    canSubmit(card: BugCard): boolean {
        return canSubmitBug(card);
    }

    /** What goes with the report besides the text the user sees. */
    bugIncludes(card: BugCard): string {
        const parts = ['your user, tenant and app version'];
        if (card.contexts?.length) {
            parts.push(card.contexts.length === 1 ? 'the attached screen details' : `${card.contexts.length} attached screens`);
        }
        if (card.screenshots?.length) {
            parts.push(card.screenshots.length === 1 ? '1 screenshot' : `${card.screenshots.length} screenshots`);
        }
        return 'Sent with: ' + parts.join(', ') + '.';
    }

    /** Submit (explicit click): the server files the report as a ClickUp task and returns its link. */
    async submitBug(message: ChatMessage): Promise<void> {
        const session = this.sessionOf(message);
        const card = message.bug;
        if (!session || !canSubmitBug(card)) {
            return;
        }
        card.state = 'submitting';
        card.error = undefined;
        this.persist();
        try {
            const res = await this.assistantService.reportBug(session.key, bugReportBody(card));
            card.state = 'submitted';
            card.taskUrl = typeof res?.taskUrl === 'string' && /^https?:\/\//i.test(res.taskUrl) ? res.taskUrl : undefined;
            card.warning = typeof res?.warning === 'string' && res.warning.trim() ? res.warning.trim() : undefined;
        } catch (e) {
            card.state = 'editing';
            card.error = bugSubmitError(e);
        }
        this.persist();
    }

    dismissBug(message: ChatMessage): void {
        if (message.bug && message.bug.state !== 'submitting') {
            message.bug.state = 'dismissed';
            this.persist();
        }
    }

    /** "Report a bug" under the latest reply when it is about an error (or the chat itself failed). */
    offerBugReport(message: ChatMessage, index: number): boolean {
        if (!this.bugReportEnabled || message.role !== 'assistant' || message.pending || message.note || message.screen || message.bug) {
            return false;
        }
        const messages = this.activeMessages;
        for (let j = messages.length - 1; j > index; j--) {
            if (messages[j].role === 'assistant' && !messages[j].note && !messages[j].screen) {
                return false; // only the latest reply offers it
            }
        }
        const question = messages
            .slice(0, index)
            .reverse()
            .find((m) => m.role === 'user');
        if (question?.display) {
            return false; // the reply to a "Report a bug" turn (it has or had the form)
        }
        return explainsError(message.text, question?.text, message.error);
    }

    // ----- what changed + undo (item 8) -------------------------------------------------------------

    changeLinesOf(message: ChatMessage): string[] {
        return message.changes ? changeLines(message.changes.items) : [];
    }

    canUndoChanges(card: ChangesCard): boolean {
        return canUndo(this.undoView(card, this.activeSession), !!this.activeSession?.sending);
    }

    undoLabel(card: ChangesCard): string {
        return undoStateLabel(this.undoView(card, this.activeSession));
    }

    /** The card as shown: 'proposed' counts as idle once its revert plan expired, was cancelled or is gone. */
    private undoView(card: ChangesCard, session: ChatSession | null | undefined): ChangesCard {
        return card?.undo === 'proposed' ? effectiveUndoCard(card, undoPlanFor(card, session?.messages || [])) : card;
    }

    /** Undo → the server proposes a revert plan; it shows as a normal plan card the user approves or cancels. */
    async undoChanges(message: ChatMessage): Promise<void> {
        const session = this.sessionOf(message);
        const card = message.changes;
        if (!session || !canUndo(this.undoView(card, session), !!session.sending)) {
            return;
        }
        card.undo = 'loading';
        card.note = undefined;
        this.persist();
        try {
            const res = await this.assistantService.undoChanges(session.key, card.taskId);
            const plan = toPlanCard(undoPlanOf(res));
            if (!plan) {
                card.undo = 'unavailable';
                card.note = 'Nothing to undo for this change';
            } else {
                card.undo = 'proposed';
                session.messages.push({
                    role: 'assistant',
                    text: 'Here is how I would undo that change. Nothing is changed until you approve it.',
                    plan
                });
                this.setPlanMode(true);
                if (session.key === this.activeKey) {
                    this.scrollToBottom();
                }
            }
        } catch (e) {
            const gone = undoErrorState(e?.status);
            if (gone) {
                card.undo = gone.state;
                card.note = serverReason(e) || gone.note;
            } else {
                card.undo = 'idle';
                card.note = serverReason(e) || "Couldn't prepare the undo. Try again.";
            }
        }
        this.persist();
    }

    // ----- plan → approve ---------------------------------------------------------------------------

    /** Approve/Cancel are usable only on a live pending plan while no turn runs in the active session. */
    canAct(plan: PlanCard): boolean {
        return canActOnPlan(plan, !!this.activeSession?.sending);
    }

    planLabel(plan: PlanCard): string {
        return planStateLabel(plan);
    }

    /** Approve → the server starts a write turn for exactly this plan; its reply is polled like any chat turn. */
    async approvePlan(message: ChatMessage): Promise<void> {
        const session = this.sessionOf(message);
        const plan = message.plan;
        if (!session || !canActOnPlan(plan, !!session.sending)) {
            return;
        }
        plan.state = 'approving';
        const placeholder: ChatMessage = {role: 'assistant', text: '', pending: true, id: newMessageId(), createdAt: Date.now()};
        session.messages.push(placeholder);
        session.sending = true; // same-session sends queue behind the write turn
        this.persist();
        if (session.key === this.activeKey) {
            this.scrollToBottom();
        }

        // like a message: only what the user attached goes along (and is then cleared); nothing otherwise
        const pending = this.attachments.take(session.key);
        try {
            const started = await this.assistantService.approvePlan(session.key, plan.id, turnPayload(pending));
            if (started?.error || !started?.taskId) {
                plan.state = 'pending';
                this.restorePending(session, pending, placeholder);
                this.failPlaceholder(session, placeholder, started?.error || `${ASSISTANT_LABEL} could not start the approved change. Try again.`);
                return;
            }
            plan.state = 'approved';
            this.markTurn([placeholder], started);
            if (undoTaskIdOf(plan.id)) {
                // a revert plan: the original card reads "undone" only once this turn completes (see pollTask)
                this.undoTurns.set(placeholder, plan.id);
            }
            this.persist();
            this.pollTask(session, started.taskId, placeholder);
        } catch (e) {
            const detail = planErrorDetail(e);
            const gone = planGoneState(e?.status, detail);
            this.restorePending(session, pending, placeholder);
            if (gone === 'expired') {
                // 404, or 409 whose reason says the plan expired: it never ran.
                plan.state = 'expired';
                // an expired revert plan: Undo can be pressed again for a fresh one (within the 24 h window)
                this.setUndoState(session, plan.id, 'idle');
                this.failPlaceholder(
                    session,
                    placeholder,
                    'This plan is no longer available, so nothing was changed. Ask again to get a fresh plan.'
                );
            } else if (gone === 'used') {
                // 409: approved or cancelled already (another tab, a double click, or before a reload) - the change may have run.
                plan.state = 'used';
                this.failPlaceholder(
                    session,
                    placeholder,
                    detail
                        ? detail + ' It was not run again.'
                        : 'This plan was already approved or cancelled, so it was not run again. Check the latest reply to see what changed.'
                );
            } else {
                plan.state = 'pending';
                const reason = assistantErrorReason(e);
                this.failPlaceholder(
                    session,
                    placeholder,
                    reason ? `${reason} Nothing was changed.` : `Could not reach ${ASSISTANT_LABEL}. Nothing was changed — try again.`
                );
            }
        }
    }

    /** Cancel → the server drops the plan; nothing is written. */
    async cancelPlan(message: ChatMessage): Promise<void> {
        const session = this.sessionOf(message);
        const plan = message.plan;
        if (!session || !canActOnPlan(plan, !!session.sending)) {
            return;
        }
        plan.state = 'cancelling';
        try {
            await this.assistantService.cancelPlan(session.key, plan.id);
            plan.state = 'cancelled';
            // a cancelled revert plan: Undo can be pressed again
            this.setUndoState(session, plan.id, 'idle');
        } catch (e) {
            const gone = planGoneState(e?.status, planErrorDetail(e));
            if (gone) {
                plan.state = gone;
                if (gone === 'expired') {
                    this.setUndoState(session, plan.id, 'idle');
                }
            } else {
                plan.state = 'pending';
                console.error('Assistant plan cancel failed', e);
                // api/assistant/** errors skip the app-wide dialog, so explain the failure in the chat
                const reason = assistantErrorReason(e);
                this.showAttachHint(reason ? `Couldn't cancel the plan: ${reason}` : "Couldn't cancel the plan. Try again.");
            }
        }
        this.persist();
    }

    /** A revert plan (`undo.<taskId>`) moved on: update the "What changed" card of that approved turn. */
    private setUndoState(session: ChatSession, planId: string, state: 'idle' | 'undone'): void {
        const taskId = undoTaskIdOf(planId);
        const card = taskId ? session.messages.find((m) => m.changes?.taskId === taskId)?.changes : null;
        if (card && card.undo === 'proposed') {
            card.undo = state;
            card.note = undefined;
        }
    }

    private sessionOf(message: ChatMessage): ChatSession {
        return this.sessions.find((s) => s.messages.includes(message));
    }

    private latestNarrative(status: AssistantTaskStatus): string {
        const events = status?.metadata?.events;
        if (!Array.isArray(events)) {
            return '';
        }
        for (let i = events.length - 1; i >= 0; i--) {
            if (events[i]?.kind === 'narrative' && events[i]?.text) {
                return events[i].text;
            }
        }
        return '';
    }

    private failPlaceholder(session: ChatSession, placeholder: ChatMessage, message: string): void {
        const undoPlanId = this.undoTurns.get(placeholder);
        if (undoPlanId) {
            // the approved revert turn failed or was cancelled: nothing was reverted, so Undo is offered again
            this.undoTurns.delete(placeholder);
            this.setUndoState(session, undoPlanId, 'idle');
        }
        placeholder.pending = false;
        this.setActivity(placeholder, null);
        placeholder.error = true;
        placeholder.text = message;
        this.finishTurn(session);
    }

    // ----- live activity hint (RMA-61) ---------------------------------------------------------------

    /** "Running a report query · step 3" of a pending reply (translated label + step). */
    activityTextOf(m: ChatMessage): string {
        return m?.activity ? activityText(m.activity, (key, params) => this.translate.instant(key, params)) : '';
    }

    /** CU-14zefhy29vq: the translated label of a pending reply (the part that is cut with … when too long). */
    activityLabelOf(m: ChatMessage): string {
        return m?.activity ? activityLabel(m.activity, (key, params) => this.translate.instant(key, params)) : '';
    }

    /** CU-14zefhy29vq: " · step 3" of a pending reply (never cut). */
    activityStepOf(m: ChatMessage): string {
        return m?.activity ? activityStep(m.activity, (key, params) => this.translate.instant(key, params)) : '';
    }

    /** " · 42s" of a pending reply, ticking every second. */
    activityElapsedOf(m: ChatMessage): string {
        return m?.activity ? activityElapsed(m.activity, this.activityNow) : '';
    }

    /** Shows (or clears with null) the activity of a pending reply; the 1 s timer runs only while one is shown. */
    private setActivity(placeholder: ChatMessage, activity: AssistantActivityView | null): void {
        if (activity) {
            placeholder.activity = activity;
            this.activityNow = Date.now();
        } else {
            delete placeholder.activity;
        }
        this.syncActivityTimer();
    }

    private hasLiveActivity(): boolean {
        return this.sessions.some((s) => (s.messages || []).some((m) => m.pending && !!m.activity));
    }

    private syncActivityTimer(): void {
        if (!this.hasLiveActivity()) {
            this.stopActivityTimer();
            return;
        }
        if (!this.activityTimer) {
            this.activityTimer = setInterval(() => {
                if (!this.hasLiveActivity()) {
                    this.stopActivityTimer();
                    return;
                }
                this.activityNow = Date.now();
            }, 1000);
        }
    }

    private stopActivityTimer(): void {
        if (this.activityTimer) {
            clearInterval(this.activityTimer);
            this.activityTimer = null;
        }
    }

    private finishTurn(session: ChatSession): void {
        session.sending = false;
        session.lastActivityAt = Date.now();
        session.taskId = undefined;
        // a screen request can only be answered while its turn runs
        session.messages.forEach((m) => {
            if (m.screen?.state === 'asking') {
                m.screen.state = 'expired';
            }
        });
        this.stopPolling(session);
        this.persist();
        if (session.key === this.activeKey) {
            this.scrollToBottom();
            // the reply may have changed what is on screen (an approved change, a new error)
            this.refreshContext();
        }
        // drain anything the user queued in this session while the turn was running
        this.processQueue(session);
    }

    private stopPolling(session: ChatSession): void {
        if (session && session.pollHandle) {
            clearTimeout(session.pollHandle);
            session.pollHandle = null;
        }
    }

    private stopAllPolling(): void {
        this.sessions.forEach((s) => this.stopPolling(s));
    }

    // ----- CU-14zefhy26bz: feedback, copy, stop, retry, UI events, "Did this solve it?" ----------------------

    /** Ids of the turn on the messages that belong to it. */
    private markTurn(messages: ChatMessage[], started: {taskId?: string; turnId?: string}): void {
        messages.forEach((m) => {
            m.taskId = started.taskId;
            if (started.turnId) {
                m.turnId = started.turnId;
            }
        });
    }

    /** Queues a UI event (sent in a batch every 10 s, on close); never blocks, never throws. */
    private track(name: string, props?: any, session?: ChatSession, turnId?: string): void {
        try {
            const s = session || this.activeSession;
            if (s && this.claudyEnabled) {
                this.uiEvents.add(s.key, name, props, turnId);
            }
        } catch (e) {
            // analytics must never get in the way of the chat
        }
    }

    private flushEvents(): void {
        if (!this.uiEvents.size) {
            return;
        }
        for (const batch of this.uiEvents.take()) {
            this.assistantService.sendEvents(batch.session, batch.events, batch.turnId).catch(() => undefined); // dropped on failure
        }
    }

    private startEventTimer(): void {
        if (this.eventTimer) {
            return;
        }
        this.eventTimer = setInterval(() => {
            this.flushEvents();
            const active = this.activeSession;
            if (active && this.assistantPopover?.isOpen()) {
                this.offerResolution(active, 'idle');
            }
        }, EVENT_FLUSH_MS);
    }

    private trackSuggestionsShown(): void {
        if (!this.assistantPopover?.isOpen() || !this.activeSession) {
            return;
        }
        const list = this.shownSuggestions;
        const key = this.activeKey + '|' + list.join('|');
        if (list.length && key !== this.suggestionsShownKey) {
            this.suggestionsShownKey = key;
            this.track('suggestion_shown', {count: list.length});
        }
    }

    closeNotice(): void {
        dismissNotice(this.noticeStoreKey);
        this.noticeVisible = false;
        this.track('notice_dismissed');
    }

    // thumbs, copy

    /** The thumbs, copy and retry row: under a finished reply of the assistant (not a note, a screen card or a stopped turn). */
    showActions(m: ChatMessage): boolean {
        return m.role === 'assistant' && !m.pending && !m.error && !m.note && !m.screen && !m.stopped && !!m.text;
    }

    /** A reply can be rated once the server gave its turn an id (an older API or an unrecorded turn has none). */
    canRate(m: ChatMessage): boolean {
        return !!m.turnId;
    }

    rate(m: ChatMessage, thumb: Thumb): void {
        const session = this.sessionOf(m);
        if (!session || !m.turnId || !applyRating(session.messages, m, thumb, session)) {
            return;
        }
        this.persist();
        this.assistantService
            .sendFeedback(session.key, thumbBody(m.turnId, thumb))
            .then(() => {
                if (m.feedback) {
                    m.feedback.sent = true;
                }
            })
            .catch(() => undefined); // optional: a failed thumb is dropped
    }

    reasonOn(m: ChatMessage, code: string): boolean {
        return !!m.feedback?.reasons?.includes(code);
    }

    pickReason(m: ChatMessage, code: string): void {
        if (m.feedback) {
            m.feedback.reasons = toggleReason(m.feedback.reasons, code);
        }
    }

    /** Sends the chosen reasons / comment (a second row for the same turn: the later one is the full answer) and closes the chips. */
    sendReasons(m: ChatMessage): void {
        const session = this.sessionOf(m);
        const fb = m.feedback;
        if (!session || !fb || !m.turnId) {
            return;
        }
        const body = reasonsBody(m.turnId, fb);
        if (body) {
            this.assistantService.sendFeedback(session.key, body).catch(() => undefined);
        }
        this.persist();
    }

    closeReasons(m: ChatMessage): void {
        if (m.feedback) {
            m.feedback.open = false;
            this.persist();
        }
    }

    async copyReply(m: ChatMessage): Promise<void> {
        const text = m.display || m.text || '';
        let payload: {plain: string; html: string} = {plain: text, html: ''};
        if (m.role === 'assistant' && !m.error && m.text) {
            try {
                if (!this.routeRoots) {
                    this.routeRoots = topLevelRoutes(this.router.config);
                }
                const origin = typeof location !== 'undefined' ? location.origin : '';
                payload = copyPayload(m.text, (piece) => splitLinks(piece, origin, this.routeRoots, (path) => this.isAppRoute(path)), origin);
            } catch (e) {
                payload = {plain: text, html: ''};
            }
        }
        let ok = false;
        try {
            ok = await copyFormatted(
                payload,
                typeof navigator !== 'undefined' ? navigator.clipboard : null,
                typeof window !== 'undefined' ? (window as any).ClipboardItem : null
            );
        } catch (e) {
            ok = false;
        }
        if (!ok) {
            ok = this.copyFallback(payload.plain);
        }
        if (ok) {
            m.copied = true;
            setTimeout(() => (m.copied = false), 1500);
            this.track('reply_copied', undefined, this.sessionOf(m), m.turnId);
        }
    }

    private copyFallback(text: string): boolean {
        try {
            const area = document.createElement('textarea');
            area.value = text;
            area.style.position = 'fixed';
            area.style.opacity = '0';
            document.body.appendChild(area);
            area.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(area);
            return ok;
        } catch (e) {
            return false;
        }
    }

    // ----- W4: the period of an answer, W3: tables, branches, edit and resend ------------------------

    /** W4 (C2): asks the team for the full history behind a limited answer (a task is filed server side; no email). */
    async requestHistory(m: ChatMessage): Promise<void> {
        const session = this.sessionOf(m);
        const dw = m.dataWindow;
        if (!session || !dw || m.historyAsk?.state === 'sending') {
            return;
        }
        const before = session.messages.slice(0, session.messages.indexOf(m));
        const asked = [...before].reverse().find((x) => x.role === 'user');
        m.historyAsk = {state: 'sending'};
        try {
            const done = await this.assistantService.requestHistory({
                session: session.key,
                question: (asked?.display || asked?.text || '').substring(0, 1000),
                from: dw.from,
                to: dw.to
            });
            m.historyAsk = {state: 'sent', taskUrl: done?.taskUrl};
        } catch (e) {
            m.historyAsk = {state: 'failed', error: assistantErrorReason(e) || 'Could not send your request. Try again.'};
        }
        this.persist();
    }

    /** W3: saves one table of a reply as a CSV file (opens in Excel). */
    downloadTable(m: ChatMessage, block: MdBlock): void {
        if (block.kind !== 'table') {
            return;
        }
        const tables = (this.markdownOf(m) || []).filter((b) => b.kind === 'table');
        const index = Math.max(1, tables.indexOf(block) + 1);
        const ok = downloadCsv(typeof document !== 'undefined' ? document : null, csvFileName(index), tableToCsv(block));
        this.track('table_csv_downloaded', {ok, rows: block.rows.length}, this.sessionOf(m), m.turnId);
        if (!ok) {
            this.noteActive('Could not save the table. Try again.');
        }
    }

    /** A transcript note in the open chat (local only; never sent to the assistant). */
    private noteActive(text: string): void {
        const session = this.activeSession;
        if (!session) {
            return;
        }
        session.messages.push({role: 'assistant', note: true, text});
        this.persist();
        this.scrollToBottom();
    }

    /** "Branch 2 of 3" for the chat on screen when it belongs to a family of branches; '' otherwise. */
    get branchLabel(): string {
        const s = this.activeSession;
        if (!s || this.sessions.length < 2) {
            return '';
        }
        const family = branchFamily(
            this.sessions.map((x) => ({key: x.key, forkedFrom: x.forkedFrom})),
            s.key
        );
        return branchPosition(family, s.key);
    }

    /** Offered on any finished question or answer (the exact check is made on click, it needs the stored transcript). */
    canBranchHere(m: ChatMessage): boolean {
        return !m.pending && !m.editing && !m.note && !m.screen && !this.forking;
    }

    async branchFrom(m: ChatMessage): Promise<void> {
        const session = this.sessionOf(m);
        if (!session || !forkRequestFrom(session, m)) {
            this.noteActive('This message cannot start a branch.');
            return;
        }
        this.track('branch_clicked', undefined, session, m.turnId);
        await this.openFork(session, () => forkRequestFrom(session, m));
    }

    /**
     * Stores the chat, forks it on the server and opens the new chat; null (with a note in the chat) when it fails. The request
     * is built AFTER the save (a 409 reload swaps the messages) and nothing is forked when the save did not land.
     */
    private async openFork(
        session: ChatSession,
        buildRequest: () => {session: string; fromMessageIndex: number} | null
    ): Promise<ChatSession | null> {
        if (this.forking) {
            return null;
        }
        this.forking = true;
        const epoch = this.epoch;
        try {
            await this.saveNow(session); // the server forks the copy it holds
            if (this.historyDirty.has(session.key) || session.unsynced || !this.sessions.includes(session)) {
                this.noteActive('Could not save this chat, so it cannot be branched now. Try again.');
                return null;
            }
            const request = buildRequest();
            if (!request) {
                this.noteActive('This message cannot start a branch.');
                return null;
            }
            const result = await this.assistantService.forkChat(request);
            if (!result?.session) {
                throw new Error('no session');
            }
            if (epoch !== this.epoch) {
                return null;
            }
            this.closedKeys = this.closedKeys.filter((k) => k !== result.session);
            await this.loadTranscript(result.session, {label: result.label, updatedAt: Date.now()});
            const branch = this.sessions.find((x) => x.key === result.session);
            if (epoch !== this.epoch) {
                return null;
            }
            if (!branch) {
                this.noteActive('The branch was made but could not be opened. Find it in the history.');
                return null;
            }
            branch.forkedFrom = branch.forkedFrom || session.key;
            this.persist();
            this.selectSession(branch.key);
            return branch;
        } catch (e) {
            if (this.sessions.includes(session)) {
                this.noteActive(assistantErrorReason(e) || 'Could not branch this chat. Try again.');
            }
            return null;
        } finally {
            this.forking = false;
        }
    }

    /** Writes the chat's transcript now (a pending upload is brought forward). */
    private async saveNow(session: ChatSession): Promise<void> {
        this.persist();
        if (session.unsynced || session.revision === undefined) {
            this.historyDirty.add(session.key);
        }
        clearTimeout(this.historyTimer);
        if (this.historyDirty.has(session.key)) {
            await this.saveHistory();
        }
    }

    /** "Edit and resend": the question turns into an editable box. Not offered for a question with attachments or a bug draft. */
    canEdit(m: ChatMessage): boolean {
        return m.role === 'user' && !m.pending && !m.editing && !m.display && !m.attachments?.length && !this.forking;
    }

    startEdit(m: ChatMessage): void {
        if (!this.canEdit(m)) {
            return;
        }
        m.editing = true;
        m.editText = m.text;
        this.track('edit_clicked', undefined, this.sessionOf(m), m.turnId);
    }

    cancelEdit(m: ChatMessage): void {
        m.editing = false;
        delete m.editText;
    }

    /** Sends the edited question in a branch that keeps everything before it (a new chat when it was the first question). */
    async resendEdited(m: ChatMessage): Promise<void> {
        const session = this.sessionOf(m);
        const text = (m.editText || '').trim();
        if (!session || !text || this.forking) {
            return;
        }
        const plan = editResendPlan(session, m);
        if (!plan.ok) {
            this.cancelEdit(m);
            this.noteActive('This question cannot be edited any more.');
            return;
        }
        let target: ChatSession | null;
        if (plan.fork) {
            target = await this.openFork(session, () => editResendPlan(session, m).fork);
        } else {
            let key = 's' + Date.now();
            while (this.sessions.some((x) => x.key === key)) {
                key += 'n';
            }
            target = {
                key,
                label: freeSessionLabel(
                    this.sessions.map((x) => x.label),
                    this.sessions.length + 1
                ),
                messages: []
            };
            this.sessions.push(target);
            this.files = [];
            this.activeKey = key;
            this.persist();
        }
        if (!target) {
            return;
        }
        this.cancelEdit(m);
        this.track('edit_resent', undefined, session, m.turnId);
        (target.queue = target.queue || []).push({text, pending: [], entryPoint: 'typed', tier: this.tier});
        this.persist();
        if (!target.sending) {
            this.processQueue(target);
        }
    }

    // stop, retry

    /** Stop while the turn runs: the server cancels the task; the next poll reads `cancelled` and the bubble says "Stopped.". */
    async stopTurn(m: ChatMessage): Promise<void> {
        const session = this.sessionOf(m);
        if (!session || !m.pending || !m.taskId || m.stopped) {
            return;
        }
        this.track('stop_clicked', undefined, session, m.turnId);
        // a refused stop (not running any more / not ours) puts the bubble back: the poll shows what became of the turn
        await requestStop(m, () => this.assistantService.stopTurn(session.key, m.taskId as string));
    }

    retryAvailable(index: number): boolean {
        const session = this.activeSession;
        return !!session && !session.sending && retryText(session.messages, index) !== null;
    }

    /** Retry on a failed turn: the same message again, asking for a better model (`escalate`). */
    retry(m: ChatMessage, index: number): void {
        const session = this.sessionOf(m);
        const text = session ? retryText(session.messages, index) : null;
        if (!session || text === null || session.sending) {
            return;
        }
        this.track('retry_clicked', undefined, session, m.turnId);
        session.messages.splice(index, 1);
        // the screens / screenshots the failed turn carried go again (kept in memory only: lost after a reload)
        (session.queue = session.queue || []).push(retryEntry(text, this.turnPending.get(m)));
        this.persist();
        this.processQueue(session);
    }

    // "Did this solve it?"

    /** Shows the question for this chat when the rules allow (once per chat, >= 1 finished turn, no running turn, idle 10 min). */
    private offerResolution(session: ChatSession, trigger: 'close' | 'idle'): void {
        if (this.resolutionAsk) {
            return;
        }
        const ask = claimResolution(session, {
            messages: session.messages,
            sending: session.sending,
            trigger,
            now: Date.now(),
            lastActivityAt: session.lastActivityAt
        });
        if (!ask) {
            return;
        }
        this.resolutionAsk = {session: session.key};
        clearTimeout(this.resolutionTimer);
        this.resolutionTimer = setTimeout(() => this.dismissResolution(), RESOLUTION_AUTO_DISMISS_MS);
        this.persist();
    }

    answerResolution(value: Resolution): void {
        const ask = this.resolutionAsk;
        if (!ask || ask.answered) {
            return;
        }
        this.assistantService.sendFeedback(ask.session, resolutionBody(value)).catch(() => undefined);
        ask.answered = true;
        clearTimeout(this.resolutionTimer);
        this.resolutionTimer = setTimeout(() => this.dismissResolution(), 1500);
    }

    dismissResolution(): void {
        clearTimeout(this.resolutionTimer);
        this.resolutionAsk = null;
    }

    // ----- files ------------------------------------------------------------------------------------

    /** W2: the attach button takes several files at once. */
    async onFileSelected(event: any): Promise<void> {
        const picked: File[] = Array.from(event?.target?.files || []);
        await this.uploadFiles(picked);
        if (event?.target) {
            event.target.value = '';
        }
    }

    /** W2: files dropped anywhere on the panel (the drop zone shows while they are dragged over it). */
    async onDrop(event: DragEvent): Promise<void> {
        if (!dragHasFiles(event?.dataTransfer?.types)) {
            return;
        }
        event.preventDefault();
        this.dragging = false;
        await this.uploadFiles(Array.from(event.dataTransfer?.files || []));
    }

    onDragOver(event: DragEvent): void {
        if (!dragHasFiles(event?.dataTransfer?.types)) {
            return;
        }
        event.preventDefault();
        this.dragging = true;
    }

    onDragLeave(event: DragEvent): void {
        const root = event?.currentTarget as HTMLElement | null;
        const to = event?.relatedTarget as Node | null;
        if (this.dragging && (!to || !root?.contains(to))) {
            this.dragging = false;
        }
    }

    /** W2: images (and files) pasted into the question box go up as chat files; plain text pastes as usual. */
    onPaste(event: ClipboardEvent): void {
        const pasted: File[] = Array.from(event?.clipboardData?.files || []);
        const text = event?.clipboardData?.getData('text/plain');
        // copied cells / web text come with a picture of themselves: the text wins, the browser pastes it as usual
        if (!pasted.length || (text && text.trim())) {
            return;
        }
        event.preventDefault();
        const now = Date.now();
        this.uploadFiles(pasted.map((f, i) => new File([f], pastedFileName(f.name, f.type, i, now), {type: f.type})));
    }

    private async uploadFiles(picked: File[]): Promise<void> {
        const key = this.activeKey;
        for (const file of picked.slice(0, UPLOAD_AT_ONCE_MAX)) {
            try {
                const files = await this.assistantService.uploadFile(key, file);
                if (key === this.activeKey) {
                    this.files = files; // RMA-47: the list of the tab the user switched away from is not shown on this one
                }
            } catch (e) {
                // non-fatal — chat still works without the upload; D-502: say why here (no app-wide dialog for CoPilot calls)
                console.error('Assistant file upload failed', e);
                const reason = assistantErrorReason(e);
                this.showAttachHint(reason ? `Couldn't upload ${file.name}: ${reason}` : `Couldn't upload ${file.name}. Try again.`);
            }
        }
        if (picked.length > UPLOAD_AT_ONCE_MAX) {
            this.showAttachHint(`Only the first ${UPLOAD_AT_ONCE_MAX} files were added. Add the rest in a second step.`);
        }
    }

    async removeFile(file: any): Promise<void> {
        const path = file?.path || file;
        const key = this.activeKey;
        try {
            const files = await this.assistantService.deleteFile(key, path);
            if (key === this.activeKey) {
                this.files = files;
            }
        } catch (e) {
            console.error('Assistant file delete failed', e);
            const reason = assistantErrorReason(e);
            this.showAttachHint(reason ? `Couldn't remove the file: ${reason}` : "Couldn't remove the file. Try again.");
        }
    }

    private async refreshFiles(): Promise<void> {
        // RMA-47: switching tabs quickly starts several lookups; only the answer for the tab on show is used
        const key = this.activeKey;
        try {
            const files = await this.assistantService.listFiles(key);
            if (key === this.activeKey) {
                this.files = files;
            }
        } catch (e) {
            if (key === this.activeKey) {
                this.files = [];
            }
        }
    }

    fileName(file: any): string {
        const path = file?.path || String(file);
        return path.includes('/') ? path.substring(path.lastIndexOf('/') + 1) : path;
    }

    // ----- persistence ------------------------------------------------------------------------------

    private restore(): void {
        this.sessions = [];
        this.closedKeys = [];
        try {
            // RMA-12: only this tenant + login's own key; no identity yet = nothing is read
            const raw = this.storageKey ? localStorage.getItem(this.storageKey) : null;
            if (raw) {
                const parsed = JSON.parse(raw);
                this.sessions = parsed.sessions || [];
                this.activeKey = parsed.activeKey || '';
                this.closedKeys = Array.isArray(parsed.closedKeys) ? parsed.closedKeys.filter((k: any) => typeof k === 'string') : [];
            }
        } catch (e) {
            this.sessions = [];
        }
        if (!this.sessions.length) {
            this.sessions = [{key: 's' + Date.now(), label: 'Session 1', messages: []}];
        }
        // never resume a half-finished assistant turn (or a stale queue) from a previous load
        this.sessions.forEach((s) => {
            s.messages = this.restoredMessages(s.messages);
            s.sending = false;
            s.queue = [];
            s.pollHandle = null;
            s.taskId = undefined;
            // screen consent lasts only for the live page: after a reload the user is asked again
            s.screenConsent = false;
            // older transcripts kept a camera shot "for the next message" here; pending pills are never restored
            delete (s as any).pendingShot;
            // the links hint goes again with the first message after a reload (lead decision 1)
            s.routesHint = undefined;
        });
        if (!this.sessions.find((s) => s.key === this.activeKey)) {
            this.activeKey = this.sessions[0].key;
        }
        // what is stored now is the baseline: only later changes bump `updatedAt` and upload
        this.sessions.forEach((s) => this.historyPrints.set(s.key, this.historyPrint(s)));
    }

    /** Messages from storage (this browser or the server): no half-finished turn, and in-flight card states reset. */
    private restoredMessages(messages: ChatMessage[]): ChatMessage[] {
        const list = (messages || []).filter((m) => m && !m.pending);
        list.forEach((m) => {
            delete m.activity; // RMA-61: transient
            delete m.copied;
            delete m.editing; // W3: an open edit box does not survive a reload
            delete m.editText;
            if (m.historyAsk?.state === 'sending') {
                delete m.historyAsk; // W4: a request that was on its way when the page went is unknown: offered again
            }
            if (m.feedback?.open) {
                m.feedback.open = false; // the reason chips do not survive a reload
            }
            if (!m.id) {
                m.id = newMessageId();
            }
            if (m.plan) {
                m.plan = restorePlanCard(m.plan);
            }
            if (m.bug) {
                m.bug = restoreBugCard(m.bug);
            }
            if (m.changes) {
                m.changes = restoreChangesCard(m.changes);
            }
            // a screen request never survives a reload (its turn is gone)
            if (m.screen && (m.screen.state === 'asking' || m.screen.state === 'sharing')) {
                m.screen.state = 'expired';
            }
        });
        // a 'proposed' undo whose revert plan expired / ended before the reload offers Undo again
        settleProposedUndos(list);
        return list;
    }

    persist(): void {
        const now = Date.now();
        this.sessions.forEach((s) =>
            (s.messages || []).forEach((m) => {
                if (!m.id) {
                    m.id = newMessageId(now);
                    m.createdAt = m.createdAt || now;
                }
            })
        );
        this.trackHistoryChanges();
        if (!this.storageKey) {
            return; // RMA-12: no known user yet: keep working in memory, store nothing under a shared key
        }
        try {
            localStorage.setItem(this.storageKey, JSON.stringify({sessions: this.sessions, activeKey: this.activeKey, closedKeys: this.closedKeys}));
        } catch (e) {
            // storage full / unavailable — keep working in-memory
        }
    }

    // ----- server-side history (item 10) ------------------------------------------------------------

    /** Fingerprint of what would be stored for a session (finished messages + label). */
    private historyPrint(session: ChatSession): string {
        try {
            return JSON.stringify([session.label, !!session.pinned, session.forkedFrom || '', (session.messages || []).filter((m) => !m.pending)]);
        } catch (e) {
            return String(Date.now());
        }
    }

    /** A session whose stored content changed gets a new `updatedAt` and is queued for upload (debounced). */
    private trackHistoryChanges(): void {
        let changed = false;
        this.sessions.forEach((s) => {
            const print = this.historyPrint(s);
            if (this.historyPrints.get(s.key) === print) {
                return;
            }
            this.historyPrints.set(s.key, print);
            s.updatedAt = Date.now();
            if (s.messages.some((m) => !m.pending)) {
                this.historyDirty.add(s.key);
                s.unsynced = true; // RMA-27: survives a reload until the upload went through
                s.archived = false; // continuing an archived chat revives it: it stays in the history
                changed = true;
            }
        });
        if (changed) {
            this.scheduleHistorySave();
        }
    }

    private scheduleHistorySave(): void {
        if (!this.historyAvailable || !this.storageKey) {
            return;
        }
        clearTimeout(this.historyTimer);
        this.historyTimer = setTimeout(() => this.saveHistory(), TRANSCRIPT_SAVE_DEBOUNCE_MS);
    }

    /** Uploads changed transcripts. A failed one stays queued for the next change; 409 = the server copy is newer. */
    private async saveHistory(): Promise<void> {
        const keys = Array.from(this.historyDirty);
        this.historyDirty.clear();
        const epoch = this.epoch;
        for (const key of keys) {
            const session = this.sessions.find((s) => s.key === key);
            if (!session || !this.historyAvailable || !this.storageKey) {
                continue;
            }
            const transcript = toTranscript(session);
            try {
                const res = await this.assistantService.saveTranscript(key, transcript);
                if (epoch !== this.epoch) {
                    return; // RMA-12: another user is signed in now: this answer is not theirs
                }
                if (res?.saved === false && isDeletedOnServer(res)) {
                    // X3: closed on another device meanwhile; deletes win, so it goes here too
                    this.dropDeletedSessions([key]);
                } else if (res?.saved === false) {
                    // the server copy is newer (another browser / device): take it
                    await this.loadTranscript(key, null);
                } else {
                    if (!session.updatedAt) {
                        session.updatedAt = transcript.updatedAt;
                    }
                    // RMA-27: the server's revision of what was just stored is the base of the next save (T5: the API's `rev`)
                    const revision = readRevision(res);
                    if (revision !== undefined) {
                        session.revision = revision;
                    }
                    // unsynced stays set when the session changed again while this upload ran (it is queued again)
                    session.unsynced = this.historyDirty.has(key);
                }
            } catch (e) {
                if (epoch !== this.epoch) {
                    return;
                }
                if (isDeletedOnServer(e)) {
                    this.dropDeletedSessions([key]); // X3: refused because the chat was closed on another device
                } else if (e?.status === 404 || e?.status === 405 || e?.status === 501) {
                    this.historyAvailable = false; // older API without history: this browser keeps it as before
                } else if (e?.status === 409) {
                    await this.loadTranscript(key, null);
                } else {
                    this.historyDirty.add(key);
                }
            }
        }
        this.persist(); // keeps the local cache in step with anything taken from the server
    }

    /**
     * First open per page load: lists the caller's sessions on the server, downloads the ones that are new or newer
     * there (other browser / device) and uploads the ones that are newer here. localStorage stays as the offline cache.
     */
    private async syncHistory(): Promise<void> {
        if (this.historyChecked || !this.historyAvailable || !this.storageKey) {
            return;
        }
        this.historyChecked = true;
        const epoch = this.epoch;
        // RMA-30: the tabs that exist now; a tab the user adds while the sync runs is never "an untouched empty one"
        const startKeys = new Set(this.sessions.map((s) => s.key));
        let server;
        let deleted: ReturnType<typeof parseDeletedSessions>;
        try {
            const raw = await this.assistantService.listSessions();
            server = parseServerSessions(raw);
            deleted = parseDeletedSessions(raw);
        } catch (e) {
            if (epoch !== this.epoch) {
                return;
            }
            if (e?.status === 404 || e?.status === 405 || e?.status === 501) {
                this.historyAvailable = false;
            } else {
                this.historyChecked = false; // try again on the next open
            }
            return;
        }
        if (epoch !== this.epoch) {
            return; // RMA-12: another user is signed in now
        }
        const plan = mergePlan(this.sessions, server, this.closedKeys, deleted);
        // X3: chats closed on another device are dropped here, never uploaded again (deletes win across devices)
        this.dropDeletedSessions(plan.drop);
        this.applyArchivedFlags(server);
        let added = false;
        for (const key of plan.fetch) {
            const info = server.find((s) => s.key === key);
            added = (await this.loadTranscript(key, info)) || added;
            if (epoch !== this.epoch) {
                return;
            }
        }
        if (added) {
            this.dropEmptyLocalSessions(startKeys);
        }
        // CU-14zefhy38pt: an archived chat is unlisted on purpose; uploading it again would revive it
        const upload = plan.upload.filter((key) => !this.sessions.find((s) => s.key === key)?.archived);
        upload.forEach((key) => this.historyDirty.add(key));
        if (upload.length) {
            this.scheduleHistorySave();
        }
        this.persist();
    }

    /** Downloads one transcript and applies it unless a turn runs there. Returns true when it added a new tab. */
    private async loadTranscript(key: string, info: {label?: string; updatedAt?: number; revision?: string} | null): Promise<boolean> {
        let transcript;
        const epoch = this.epoch;
        try {
            transcript = fromTranscript(await this.assistantService.getTranscript(key), key);
        } catch (e) {
            return false;
        }
        if (!transcript || epoch !== this.epoch || this.closedKeys.includes(key)) {
            return false;
        }
        const updatedAt = Math.max(transcript.updatedAt || 0, info?.updatedAt || 0);
        const messages = this.restoredMessages(transcript.messages as ChatMessage[]);
        // RMA-27: the revision the server holds now is the base of this browser's next save
        const revision = transcript.revision ?? info?.revision;
        const existing = this.sessions.find((s) => s.key === key);
        let added = false;
        if (existing) {
            if (existing.sending || existing.queue?.length) {
                return false; // the live turn wins; its result uploads afterwards
            }
            existing.messages = messages;
            existing.label = transcript.label || existing.label;
            if (typeof transcript.pinned === 'boolean') {
                existing.pinned = transcript.pinned; // an unpin on another device clears the local pin; a server without the field keeps it
            }
            existing.forkedFrom = transcript.forkedFrom ?? existing.forkedFrom;
            existing.updatedAt = updatedAt;
            existing.revision = revision;
            existing.unsynced = false;
            this.historyPrints.set(key, this.historyPrint(existing));
        } else {
            // X1 (CU-14zefhy29pp): never two open tabs with one label (a kept new tab and this server chat)
            const from = this.sessions.length + 1;
            const placed = placeServerLabel(
                serverSessionLabel(
                    {label: transcript.label || info?.label},
                    from,
                    this.sessions.map((s) => s.label)
                ),
                this.sessions,
                from
            );
            const rename = placed.rename;
            if (rename) {
                const tab = this.sessions.find((s) => s.key === rename.key);
                if (tab) {
                    tab.label = rename.label;
                    this.historyPrints.set(tab.key, this.historyPrint(tab)); // an empty tab: nothing to upload
                }
            }
            const session: ChatSession = {
                key,
                label: placed.label,
                messages,
                updatedAt,
                revision,
                pinned: transcript.pinned,
                forkedFrom: transcript.forkedFrom
            };
            this.sessions.push(session);
            this.historyPrints.set(key, this.historyPrint(session));
            added = true;
        }
        this.historyDirty.delete(key);
        return added;
    }

    /**
     * X3 (CU-14zefhy1r83): drops local chats that were closed on another device (the server keeps their tombstone) and
     * remembers them as closed here, so they are never uploaded again. A chat with a turn still running stays until the
     * turn ends (its upload is then refused and drops it). The last tab is replaced by a fresh empty one.
     */
    private dropDeletedSessions(keys: string[]): void {
        const gone = new Set(
            (keys || []).filter((key) => {
                const s = this.sessions.find((x) => x.key === key);
                return !s || (!s.sending && !s.queue?.length);
            })
        );
        if (!gone.size) {
            return;
        }
        gone.forEach((key) => {
            this.closedKeys = addClosedKey(this.closedKeys, key);
            this.historyDirty.delete(key);
            this.historyPrints.delete(key);
        });
        const before = this.sessions.length;
        this.sessions.forEach((s) => {
            if (gone.has(s.key)) {
                this.stopPolling(s);
                this.attachments.clear(s.key);
            }
        });
        this.sessions = this.sessions.filter((s) => !gone.has(s.key));
        if (this.sessions.length === before) {
            this.persist();
            return;
        }
        if (!this.sessions.length) {
            const key = 's' + Date.now();
            this.sessions = [{key, label: 'Session 1', messages: []}];
            this.historyPrints.set(key, this.historyPrint(this.sessions[0]));
        }
        if (!this.sessions.some((s) => s.key === this.activeKey)) {
            this.activeKey = this.sessions[0].key;
            this.refreshFiles();
        }
        this.persist();
    }

    /** After server sessions arrived: an untouched empty local tab ("Session 1" on a new device) is dropped. */
    private dropEmptyLocalSessions(startKeys: Set<string>): void {
        // RMA-30: only an untouched empty tab that existed when the sync started goes; one added meanwhile stays
        const keep = sessionsToKeepAfterSync(
            this.sessions,
            startKeys,
            (s) => this.attachments.get(s.key).length > 0,
            (s) => s.key === this.activeKey && !!this.draft.trim()
        );
        if (!keep.length || keep.length === this.sessions.length) {
            return;
        }
        const activeGone = !keep.some((s) => s.key === this.activeKey);
        this.sessions = keep;
        if (activeGone) {
            const newest = keep.reduce((a, b) => ((b.updatedAt || 0) > (a.updatedAt || 0) ? b : a));
            this.activeKey = newest.key;
            this.refreshFiles();
        }
        this.scrollToBottom();
    }

    private scrollToBottom(): void {
        setTimeout(() => {
            const el = this.scrollArea?.nativeElement;
            if (el) {
                el.scrollTop = el.scrollHeight;
            }
        }, 0);
    }
}
